import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { createClient, ApiError } from "@kundeninfo/api-client";
import type { KundeListe as KundeListeEintrag, KundeAkte as KundeAkteDaten,
              Unternehmen, WeitererTreffer } from "@kundeninfo/api-client";
import { MeSchale, useEinspalter, applyTheme, setzeAktionsfarben, Icon, useFocusTrap,
         UMGEBUNGSFARBEN, schriftAus, geraetErmitteln, useTelefon, verlassenFreigeben } from "@kundeninfo/ui";
import type { PanelState } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { KundenListe } from "./views/KundenListe.js";
import type { KundenFehler } from "./views/KundenListe.js";
import { AKTEN_TABS, KundenAkte } from "./views/KundenAkte.js";
import { FremdeAkte, istFremdeAkte } from "./views/FremdeAkte.js";
import type { AktenTabId } from "./views/KundenAkte.js";
import { KundeAnlegen } from "./views/KundeAnlegen.js";
import { Kopfzeile, Wortmarke } from "./views/Kopfzeile.js";
import { Tableiste, bereicheFuer, zielRoute } from "./views/Navigation.js";
import type { NavigationsZiel } from "./views/Navigation.js";
import { Kontext } from "./views/Kontext.js";
import { useSchnittstellen } from "./views/Schnittstellen.js";
import { useBereichAngebote } from "./views/BereichAngebote.js";
import { useBereichAuftraege } from "./views/BereichAuftraege.js";
import type { AuftragAusAngebot } from "./views/BereichAuftraege.js";
import { useBereichArtikel } from "./views/BereichArtikel.js";
import { useBereichRechnungen } from "./views/BereichRechnungen.js";
import type { RechnungAusAuftrag } from "./views/BereichRechnungen.js";
import { BereichTickets } from "./views/tickets/BereichTickets.js";
import { BereichKalender } from "./views/kalender/BereichKalender.js";
import { Cockpit } from "./views/cockpit/Cockpit.js";
import { COCKPIT_TEXT_WEB } from "./views/cockpit/cockpitTexts.js";
import { Glocke } from "./views/benachrichtigungen/Glocke.js";
import { EinstellungenBenachrichtigungen } from "./views/benachrichtigungen/EinstellungenBenachrichtigungen.js";
import { EinstellungenKalender } from "./views/kalender/EinstellungenKalender.js";
import { FremdkontenBlock } from "./views/fremd/FremdkontenBlock.js";
import { aktuellesAbo, browserUmgebung, mitFrist, pushAbstimmen } from "./views/benachrichtigungen/pushGeraet.js";
import { INCOMING_FILTER, RECHNUNGS_FILTER, SUPPLIER_CONTRACT_FILTER, alleFilter, filterForStates,
  statusseZuFilter } from "./bereichsfilter.js";
import type { AngebotsFilter, AuftragsFilter, RechnungsFilter } from "./bereichsfilter.js";
import { Einstellungen } from "./views/Einstellungen.js";
import type { Preferences, EinstellungsGruppe } from "./views/Einstellungen.js";
import { EinstellungenSpalte } from "./views/EinstellungenSpalte.js";
import { Verwaltung, objektlisteVon } from "./views/Verwaltung.js";
import type { VerwaltungsBereich } from "./views/Verwaltung.js";
import { VerwaltungsSpalte } from "./views/VerwaltungsSpalte.js";
import { useVerwaltungUnternehmen } from "./views/verwaltung/unternehmen.js";
import { useVerwaltungBenutzer } from "./views/verwaltung/benutzer.js";
import { useVerwaltungKonditionen } from "./views/verwaltung/zahlungskonditionen.js";
import { useVerwaltungArtikel } from "./views/verwaltung/artikel.js";
import { useVerwaltungTextbausteine } from "./views/verwaltung/textbausteine.js";
import { useVerwaltungAngebotsvorlagen } from "./views/verwaltung/angebotsvorlagen.js";
import { useVerwaltungLayout } from "./views/verwaltung/layout.js";
import { ObjektBereich } from "./views/verwaltung/gemeinsam.js";
import type { VerwaltungBereichTeile } from "./views/verwaltung/gemeinsam.js";
import { arbeitsunternehmen, darfSperreUebernehmen, halteFensterUnternehmen, leseFensterUnternehmen,
         merkeFensterUnternehmen, aircallHinweis, aircallSicht, leseKundensuche, merkeKundensuche,
         vergissKundensuche, vergissFensterUnternehmen, seesSupplierData } from "./session.js";
import { fenster, windowTitle } from "./fenster.js";
import { BereichLiefervertraege } from "./views/suppliers/BereichLiefervertraege.js";
import { BereichEingangsrechnungen } from "./views/incomingInvoices/BereichEingangsrechnungen.js";
import { PurchasingArea } from "./views/purchasing/PurchasingArea.js";
import { PURCHASING_TAB_DEFAULT, purchasingTabFor } from "./views/purchasing/purchasingTab.js";
import { SUPPLIER_TEXT_WEB } from "./views/suppliers/texts.js";
import { useLiquidityArea } from "./views/liquidity/LiquidityArea.js";
import { LIQUIDITY_TEXT_WEB } from "./views/liquidity/liquidityTexts.js";
import { PARTNER_TEXT } from "./views/partners/texts.js";
import { leseRollenWahl, merkeRollenWahl } from "./views/partners/rollenFilter.js";
import { ALL_ROLES, SUPPLIER_TABS, visibleTabs } from "./views/aktenReiter.js";
import type { RollenWahl } from "./views/partners/rollenFilter.js";
import type { Sitzung } from "./session.js";
import { umgebungAusSitzung, titelPraefix } from "./umgebung.js";
import { anwendungsname, wirksamerZuschnitt, zuschnittAusSitzung, BELEGDESIGN_VERWALTUNG }
  from "./zuschnitt.js";
import type { Zuschnitt } from "./zuschnitt.js";
import type { UmgebungsAnzeige } from "@kundeninfo/ui";
import { STANDARD_PANELS, lesePanels, merkePanels, vergissPanels } from "./panelSpeicher.js";
import { areaLocked, isSupplierArea, isSupplierRoute, routeToPath, useRoute } from "./router.js";
import { ObjektPanelGastgeber } from "./components/objektPanel/ObjektPanelGastgeber.js";
import type { Route, Herkunft, SupplierArea, SupplierAreaFilter } from "./router.js";
import { KiKnopf } from "./components/meHuellen.js";
import "./App.css";

/**
 * Das aktive Unternehmen DIESES Fensters für den Kopf jeder Anfrage (Spec Mandantentrennung, R2).
 * Eine Modulvariable genügt: Jedes Fenster hat seine eigene Anwendung, und die Wahl wechselt nur mit
 * einem Neuladen. Bis die Sitzung steht, trägt sie die Fensterwahl (oder `null` → Server-Vorgabe);
 * danach das abgeleitete aktive Unternehmen (gesetzt beim Zeichnen von App, vor jedem Ladeeffekt).
 */
const anfrageUnternehmen: { id: string | null } = { id: leseFensterUnternehmen() };
const client = createClient("", { aktivesUnternehmen: () => anfrageUnternehmen.id });

/**
 * Die leere Unternehmensliste — EINE Instanz für alle vier Bereichs-Hooks.
 *
 * `sitzung?.companies ?? []` schrieb bei jedem Zeichnen ein FRISCHES Array in `companies`.
 * Solange die Sitzung noch nicht steht (und an jedem Zeichnen davor), war das jedes Mal
 * eine andere Referenz — und die Bereiche führen `companies` in Abhängigkeitslisten. Die
 * Wirkung ist heute harmlos, aber sie ist auch nicht zugesichert: Jede Abhängigkeitsliste,
 * die diesen Wert künftig aufnimmt, liefe grundlos mit. Eine Konstante auf Modulebene
 * kostet nichts und nimmt der Frage die Spitze.
 *
 * `Unternehmen[]` und nicht `readonly Unternehmen[]`: Die Bereiche erwarten die
 * veränderbare Gestalt aus dem Klienten; geschrieben wird hier nichts (der Wert steht in
 * keiner Zeile, die etwas hineinlegt).
 */
const LEERE_UNTERNEHMEN: Unternehmen[] = [];

/**
 * Was in der Listenspalte des Rahmens steht — die Liste, aus der man auf diesem
 * Bildschirm weiterblättert.
 *
 * Seit dem 23.09.2026 trägt JEDE Adresse der Hauptnavigation eine Spalte. Vier davon sind
 * NAVIGATION (Kundenliste, Bereichs-Untermenü der Verwaltung, Dienstliste, Gruppen-Untermenü
 * der Einstellungen), vier sind OBJEKTLISTEN (Angebote, Aufträge, Artikel, Rechnungen).
 * `null` bleibt allein der unbekannten Adresse.
 *
 * Die vier Objektbereiche hatten sie schon bis zum 07.09.2026; dann entfiel sie („Filterkopf
 * statt zweiter Spalte": alles untereinander im Inhalt), und seit dem 23.09.2026 haben sie
 * sie zurück — Entscheid Dirk am Live-System: „die Liste dann als Spalte anzeigen und die
 * Übersicht als Inhaltsbereich". Der FILTERKOPF, der am 07.09. entstand, bleibt und steht
 * jetzt oben IN dieser Spalte (`lage="spalte"`).
 */
export type SpaltenInhalt =
  "kunden" | "angebote" | "auftraege" | "artikel" | "rechnungen" | "verwaltung"
  | "schnittstellen" | "einstellungen" | "liquidity";

/**
 * Welche Liste bekommt dieser Bildschirm an die Seite? `null` heisst „gar keine" — dann
 * zeichnet der Rahmen die Spalte nicht und die Ansicht steht in voller Breite da.
 *
 * Befund Dirk vom 05.09.2026: „Kunden sollten nur im Punkt Kunden angezeigt werden." Bis
 * dahin reichte App.tsx die Kundenliste FEST in die Listenspalte des Rahmens — ein
 * Überbleibsel aus der Zeit, als „Kunden" das einzige Modul war. Auf Angeboten, Aufträgen,
 * Artikeln, Schnittstellen, in den Einstellungen und in der Verwaltung stand sie seither
 * sinnlos daneben und nahm der eigentlichen Ansicht ein Drittel der Breite weg.
 *
 * Genau DREI Adressen zählen zu „Kunden": die Liste selbst, eine geöffnete Akte und die
 * Anlege-Maske. In allen dreien ist die Kundenliste der Ort, an dem man weiterblättert.
 *
 * Diese Funktion ist die EINE Stelle, an der über den Spalteninhalt entschieden wird —
 * vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * zielRoute() oben: Kommt eine Adresse hinzu, meldet das der Typprüfer, statt sie stumm
 * der einen oder anderen Liste zuzuschlagen. Die unbekannte Adresse bekommt bewusst
 * KEINE: Dort steht nur der Hinweis, dass es die Seite nicht gibt.
 *
 * Entschieden wird allein über den Adressnamen: Ob ein Objekt geöffnet ist (`id`) oder
 * woher man kam (`herkunft`), ändert die Spalte nicht — sonst verschwände sie beim
 * Anklicken eines Eintrags. Festgehalten in test/spalteFuer.test.ts.
 */
export function spalteFuer(route: Route): SpaltenInhalt | null {
  switch (route.name) {
    case "kundenliste": case "kunde": case "kundeNeu": return "kunden";
    /*
     * Die vier OBJEKTBEREICHE haben ihre Spalte seit dem 23.09.2026 zurück: Liste links,
     * gewähltes Objekt als Inhalt rechts. Liste und geöffnetes Objekt sind DERSELBE
     * Bildschirm in zwei Zuständen und bekommen deshalb dieselbe Spalte — sonst verschwände
     * sie beim Anklicken einer Zeile. Der Statusfilter in der Adresse ändert daran ebenfalls
     * nichts: Er wählt aus, WAS in der Spalte steht, nicht OB.
     */
    case "angebote": return "angebote";
    case "auftraege": return "auftraege";
    case "artikel": return "artikel";
    case "rechnungen": return "rechnungen";
    // Die TICKETS (Aufgabe 17) haben keine Listenspalte: Board und Liste stehen in voller
    // Breite, ihre Filter tragen sie selbst. Ebenso die zwei LIEFERANTENBEREICHE (Aufgabe 22): Leiste und Liste in
    // voller Breite, ein Vorgang öffnet sich in der Akte. Und das COCKPIT: Die Wand ist sein ganzer Inhalt.
    case "cockpit": case "tickets": case "kalender": case "liefervertraege": case "eingangsrechnungen": return null;
    // Die Liquidität (Empfehlung 591): Die Positionsspalte ist die Listenspalte des Rahmens.
    case "liquidity": return "liquidity";
    // Die Verwaltung (Etappe 2, Aufgabe 1): dasselbe Muster, nur mit ihrem Bereichs-
    // Untermenü in der Spalte. Übersicht und offener Bereich bekommen DIESELBE Spalte,
    // sonst verschwände das Untermenü beim Anklicken eines Eintrags.
    case "verwaltung": return "verwaltung";
    // Etappe 3: dieselbe Regel noch einmal, für die letzten zwei Bereiche. Links die
    // Dienste bzw. die Einstellungsgruppen, rechts der gewählte — und wie überall
    // bekommen „nichts gewählt" und „etwas gewählt" DIESELBE Spalte, sonst verschwände
    // sie beim Anklicken eines Eintrags.
    case "schnittstellen": return "schnittstellen";
    case "einstellungen": return "einstellungen";
    // Die unbekannte Adresse bekommt bewusst keine: Dort steht nur der Hinweis, dass es
    // die Seite nicht gibt.
    case "unbekannt": return null;
  }
}

/**
 * Steht rechts die KUNDEN-Kontextspalte? `false` heisst „gar keine" — dann zeichnet der
 * Rahmen sie nicht und die Ansicht bekommt ihre Breite dazu (siehe `MeSchaleProps.context`).
 *
 * Review-Befund vom 06.09.2026: Bis dahin reichte App.tsx den Kundenkontext FEST an den
 * Rahmen — dasselbe Überbleibsel wie bei der Listenspalte, nur eine Spalte weiter rechts.
 * In der Verwaltung, in den Einstellungen und bei den Schnittstellen stand dort unverändert
 * „Kein Kunde ausgewählt." und nahm der eigentlichen Ansicht rund 280 Pixel weg. Sichtbar
 * wurde das an den Verwaltungs-Objektbereichen: Liste und Objekt standen erst auf sehr
 * breiten Fenstern nebeneinander, weil dem Inhalt links wie rechts eine Spalte fehlte.
 *
 * Es sind DIESELBEN drei Adressen wie bei der Kundenliste (siehe `spalteFuer`) — beide
 * Aussagen betreffen den Kunden, und ein halb betretener Kundenbereich wäre keiner. Die
 * Entscheidung steht hier trotzdem eigenständig und wird nicht aus `spalteFuer`
 * abgeleitet: Das eine sagt, WORAUS man weiterblättert, das andere, ZU WEM der Bildschirm
 * gehört. Dass beide heute deckungsgleich sind, hält zeigtKundenKontext.test.ts Adresse für
 * Adresse fest — eine künftige Abweichung wäre damit eine bewusste und keine stumme.
 *
 * Vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * `zielRoute` und `spalteFuer`: Eine neue Adresse soll der Typprüfer melden, statt ihr die
 * Kontextspalte stumm zu geben oder zu nehmen.
 *
 * Entschieden wird allein über den Adressnamen: Ob ein Kunde geöffnet ist, ändert die
 * SPALTE nicht — nur ihren Inhalt (ohne Auswahl steht dort der Hinweis). Sonst erschiene
 * sie beim Anklicken eines Eintrags und verschwände beim Zurückgehen.
 */
export function zeigtKundenKontext(route: Route): boolean {
  switch (route.name) {
    // Die drei Kunden-Adressen: die Liste, eine geöffnete Akte und die Anlege-Maske.
    case "kundenliste": case "kunde": case "kundeNeu": return true;
    // Alle übrigen Bereiche haben ihren eigenen Gegenstand — ein Kundenkontext stünde dort
    // sinnlos daneben. Die Rechnungen eingeschlossen: Ihre Liste ist seit dem Paket
    // „Rechnungsstatus" nach Status gegliedert, nicht nach Kunden.
    case "cockpit": case "angebote": case "auftraege": case "artikel": case "rechnungen": case "tickets": case "kalender":
    case "liefervertraege": case "eingangsrechnungen": case "liquidity":
    case "verwaltung": case "schnittstellen": case "einstellungen":
    // Und die unbekannte Adresse gehört zu niemandem: Dort steht nur der Hinweis, dass es
    // die Seite nicht gibt.
    case "unbekannt": return false;
  }
}

/**
 * Steht auf dem iPhone-Einspalter die SPALTE im Vordergrund (statt des Details)?
 *
 * Bis Etappe 1, Aufgabe 3 stand hier schlicht `route.name === "kundenliste"` — es gab nur
 * eine Spalte, und die gehörte den Kunden. Mit den Bereichs-Spalten gilt die Regel
 * allgemein: Solange kein Objekt gewählt ist, zeigt der Einspalter die Liste; ein
 * gewähltes Objekt füllt den Bildschirm, und der Rückweg im Kopf führt zur Liste zurück
 * (siehe `zurueckWeg`).
 *
 * Vollständig aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei
 * `spalteFuer`: Eine neue Adresse soll der Typprüfer melden.
 */
function spalteZuerst(route: Route): boolean {
  switch (route.name) {
    case "kundenliste": return true;
    /*
     * Die vier Objektbereiche: Solange kein Objekt gewählt ist, zeigt der Einspalter die
     * Liste; ein gewähltes Objekt füllt den Bildschirm, und der Rückweg im Kopf führt auf die
     * Listenadresse samt ihrem Filter zurück (siehe `zurueckWeg` — die Regel steht dort schon
     * so, sie war zwischen dem 07. und dem 23.09.2026 nur ohne Wirkung).
     *
     * Die Angabe galt schon für alle vier, bevor sie alle eine Spalte hatten (Artikel ab
     * Aufgabe 4, Angebote ab Aufgabe 5, Aufträge ab Aufgabe 6, Rechnungen ab Aufgabe 7) —
     * das war gefahrlos und keine Schlamperei: Der Rahmen zeigt ohne `list` immer das
     * Detail, ganz egal was `showList` sagt (siehe MeSchale.tsx, `p.showList && hatListe`).
     * Für einen Bereich ohne Spalte war die Angabe also wirkungslos und wurde erst mit
     * seiner Spalte wirksam. Seit Aufgabe 7 haben alle vier ihre Spalte, und die Angabe
     * gilt für alle vier mit Wirkung.
     */
    case "angebote": case "auftraege": case "artikel": case "rechnungen":
      return route.id === undefined;
    /*
     * Die Verwaltung (Etappe 2, Aufgabe 1) folgt derselben Regel — ihr „gewähltes Objekt"
     * ist der BEREICH: Ohne Bereich zeigt der Einspalter das Untermenü, ein Antippen öffnet
     * den Bereich in voller Breite, und der Rückweg im Kopf führt auf das Untermenü zurück
     * (siehe `zurueckWeg`). Genau dafür ist `bereich` optional (siehe router.ts).
     *
     * Zwischen Aufgabe 2 und dem Entscheid vom 06.09.2026 hatte sie hier eine Stufe mehr:
     * Ein Bereich MIT Objektliste klappte sie in der SPALTE auf, also blieb der Einspalter
     * dort bei der Spalte, bis ein Objekt gewählt war. Seit die Objektlisten wieder im
     * INHALT stehen, gilt die einfache Regel wieder für alle zehn Bereiche — und die Stufe
     * darunter (Liste ODER Objekt) faltet der Inhalt selbst (siehe `ObjektBereich`).
     */
    case "verwaltung": return route.bereich === undefined;
    /*
     * Die Schnittstellen und die Einstellungen (Etappe 3) folgen derselben Regel wie die
     * Objektbereiche: Ohne gewählten Dienst bzw. ohne gewählte Gruppe zeigt der Einspalter
     * die Spalte, ein Antippen öffnet den Inhalt in voller Breite, und der Rückweg im Kopf
     * führt auf die Spalte zurück (siehe `zurueckWeg`). Genau dafür sind `dienst` und
     * `gruppe` optional (siehe router.ts).
     */
    case "schnittstellen": return route.dienst === undefined;
    case "einstellungen": return route.gruppe === undefined;
    // Ein Kunde, eine Anlege-Maske, eine unbekannte Adresse: Das ist jeweils das ZIEL, das
    // der Einspalter zeigt.
    case "kunde": case "kundeNeu":
    // Das Cockpit, die Tickets und die Lieferantenbereiche haben keine Spalte — ihre Wand, ihr Board bzw. ihre Liste IST das Ziel.
    case "cockpit": case "tickets": case "kalender": case "liefervertraege": case "eingangsrechnungen":
    // Die Liquidität zeigt am Telefon zuerst den Inhalt (Spec „Telefon“); „Positionen (n)“ öffnet die Spalte (`showColumn`).
    case "liquidity":
    case "unbekannt": return false;
  }
}

/**
 * Wohin der Rückweg im Kopf des iPhone-Einspalters führt — `null` heisst „von hier führt
 * keiner", und dann zeichnet der Rahmen auch keinen Knopf (siehe `MeSchaleProps.zurueck`).
 *
 * Befund vom 05.09.2026: Auf schmalen Viewports trug der Rahmen an JEDER Adresse ausser der
 * Kundenliste einen Knopf „Zurück zur Liste", der pauschal zur KUNDENliste sprang. Das war
 * an zwei Stellen falsch:
 *
 *  - Auf den globalen Listen selbst (Angebote, Aufträge, Artikel, Schnittstellen,
 *    Einstellungen, Verwaltung). Sie sind EINSTIEGSPUNKTE der Hauptnavigation — man erreicht
 *    sie über die Tab-Leiste, nicht über einen Weg, den man zurückgehen könnte. Der Knopf
 *    versprach dort einen Ursprung, den es nicht gab, und schickte einen auf einen
 *    Bildschirm, auf dem man nie war.
 *  - Nach dem Sprung AUS einer globalen Liste in ein fremdes Ziel (Angebotssuche → Akte,
 *    Katalog → Artikelpflege). Dort gibt es einen Ursprung — nur eben nicht die Kundenliste.
 *
 * Deshalb entscheidet die HERKUNFT (router.ts), nicht die Zieladresse allein. Vollständig
 * aufgezählt statt mit einem `sonst`-Zweig, aus demselben Grund wie bei `zielRoute` und
 * `spalteFuer`: Eine neue Adresse soll der Typprüfer melden, statt sie stumm dem einen
 * oder anderen Rückweg zuzuschlagen.
 */
function zurueckWeg(route: Route): Rueckziel | null {
  switch (route.name) {
    case "kundenliste":
      return null;
    // Das COCKPIT ist die Startseite, von dort führt kein Weg zurück. Die TICKETS sind ein Einstiegspunkt der Hauptnavigation;
    // ein geöffnetes Ticket ist ein Dialog über dem Board und schließt selbst (Kreuz) — einen Rückweg im Kopf braucht es nicht.
    case "cockpit": case "tickets": case "kalender":
      return null;
    // Die Lieferantenbereiche (Aufgabe 22) sind Einstiegspunkte wie die Tickets: Ein Vorgang öffnet sich in der Akte,
    // und deren Rückweg führt mit der Herkunft hierher zurück.
    case "liefervertraege": case "eingangsrechnungen":
      return null;
    // Die Liquidität ist ein Einstiegspunkt; den Rückweg aus ihrer Spalte meldet der Bereich selbst (`back`).
    case "liquidity":
      return null;
    /*
     * Die Schnittstellen und die Einstellungen (Etappe 3): Ihre LISTE bzw. ihr Untermenü
     * bleibt ein Einstiegspunkt der Hauptnavigation — von dort führt kein Weg zurück. Ein
     * geöffneter Dienst bzw. eine geöffnete Gruppe dagegen hat immer einen Ursprung: die
     * Spalte, die sie auf dem iPhone unter sich hat.
     *
     * Eine fremde HERKUNFT gibt es hier nicht (anders als bei den Objektbereichen und der
     * Verwaltung): In diese beiden Bereiche springt niemand aus einer fremden Liste — sie
     * sind über die Hauptnavigation erreichbar und sonst gar nicht. Deshalb trägt ihre
     * Adresse auch keine (siehe router.ts).
     */
    case "schnittstellen":
      return route.dienst === undefined ? null : "schnittstellen";
    case "einstellungen":
      return route.gruppe === undefined ? null : "einstellungen";
    /*
     * Die Bereiche Angebote, Aufträge und Artikel (Etappe 1, Aufgaben 3–5 vom 05.09.2026 —
     * die Nummern gehören dem ALTEN Etappenplan und sind nicht die Aufgaben 4–7 des Plans
     * vom 23.09.2026) haben seit ihrer eigenen Objektliste zwei Zustände. Die LISTE selbst
     * bleibt ein Einstiegspunkt der Hauptnavigation — von dort führt kein Weg zurück, ausser
     * man kam über eine fremde Herkunft. Ein GEÖFFNETES Objekt dagegen hat immer einen
     * Ursprung: die Liste, die es
     * auf dem iPhone unter sich hat — genau wie die Kundenakte ihre Kundenliste. Damit hat
     * `herkunft` an diesen Adressen seinen Verwerter.
     */
    case "angebote":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      // Seit dem 07.09.2026 trägt die Adresse auch den STATUSFILTER: Der Rückweg führt auf
      // die Liste, aus der man das Objekt geöffnet hat, und das ist eine gefilterte. Ohne
      // ihn landete man auf dem iPhone nach dem Zurück in „alle Angebote" — an einem Ort,
      // an dem man nicht war. Dieselbe Regel wie beim Rechnungsfilter darunter.
      return route.filter === undefined
        ? "angebote"
        : { ziel: { name: "angebote", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.angebote };
    case "auftraege":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      return route.filter === undefined
        ? "auftraege"
        : { ziel: { name: "auftraege", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.auftraege };
    case "artikel":
      // Der Artikel bleibt ohne Filtersegment (siehe router.ts) — hier genügt der Name.
      return route.id === undefined ? (route.herkunft ?? null) : (route.herkunft ?? "artikel");
    /*
     * Die Rechnungen: wie die drei Bereiche darüber — nur führt der Rückweg aus einem
     * geöffneten Beleg auf die LISTE, aus der man ihn geöffnet hat, und das ist seit dem
     * Paket „Rechnungsstatus" eine bestimmte Statusliste. Ohne den Filter landete man auf
     * dem iPhone nach dem Zurück in „Alle Rechnungen" — an einem Ort, an dem man nicht
     * war, und die eben abgearbeitete Statusliste wäre verloren. Eine fremde Herkunft
     * geht wie überall vor.
     */
    case "rechnungen":
      if (route.id === undefined) return route.herkunft ?? null;
      if (route.herkunft !== undefined) return route.herkunft;
      return route.filter === undefined
        ? "rechnungen"
        : { ziel: { name: "rechnungen", filter: route.filter },
            beschriftung: ZURUECK_BESCHRIFTUNG.rechnungen };
    /*
     * Die Verwaltung: Ihre ÜBERSICHT ist ein Einstiegspunkt der Hauptnavigation — von dort
     * führt kein Weg zurück, ausser man kam über eine fremde Herkunft („Artikel pflegen"
     * aus dem Katalog). Ein GEÖFFNETER BEREICH dagegen hat immer einen Ursprung: das
     * Untermenü, das er auf dem iPhone unter sich hat — genau wie ein geöffnetes Angebot
     * seine Angebotsliste. Die fremde Herkunft geht vor: Wer aus dem Katalog in die
     * Artikelpflege sprang, will dorthin zurück und nicht in ein Menü, in dem er nie war.
     */
    case "verwaltung": {
      if (route.bereich === undefined) return route.herkunft ?? null;
      /*
       * Seit Aufgabe 2 gibt es eine Stufe mehr: Aus einem geöffneten OBJEKT führt der
       * Rückweg auf die Objektliste seines Bereichs — nicht auf das Untermenü, in dem der
       * Bereich nur einer von zehn Einträgen wäre. Die Beschriftung NENNT das Ziel und
       * kommt deshalb aus der Bereichstabelle (deutsche Artikel lassen sich aus dem Label
       * nicht errechnen).
       *
       * Und dieser Schritt geht der HERKUNFT VOR (Aufgabe 3): Seit der Artikelstamm eine
       * Objektebene hat, kann man über „Pflege in der Verwaltung" aus dem Katalog kommen
       * UND dort einen Artikel öffnen. Schlüge die Herkunft auch dann durch, spränge der
       * Rückweg vom geöffneten Artikel in einem Satz zurück in den Katalog und überspränge
       * die Liste, aus der man ihn gerade geöffnet hat — auf dem iPhone wäre der Weg
       * zurück in den Artikelstamm damit gar nicht gehbar. Ein Rückweg geht EINE Stufe, und
       * die Stufe über einem Verwaltungs-Objekt ist die Objektliste seines Bereichs. Die
       * Herkunft ist deshalb nicht verloren: Sie steht am Bereich ohne Objekt, also genau
       * eine Stufe weiter oben, und trägt von dort in den Katalog zurück.
       */
      const liste = objektlisteVon(route.bereich);
      if (route.id !== undefined && liste !== null) {
        return {
          ziel: {
            name: "verwaltung", bereich: route.bereich,
            // Die Herkunft REIST MIT auf die Objektliste — sonst wäre der Satz oben eine
            // Behauptung ohne Deckung: Der Rückweg würde sie fallen lassen, und von der
            // Objektliste führte kein Weg mehr in den Katalog zurück.
            ...(route.herkunft === undefined ? {} : { herkunft: route.herkunft }),
          },
          beschriftung: liste.rueckweg,
        };
      }
      return route.herkunft ?? "verwaltung";
    }
    // Die Akte hat immer einen Ursprung: die globale Liste, aus der man sprang — und sonst
    // die Kundenliste, die sie auf dem iPhone unter sich hat.
    // Aus einem Lieferantenbereich mit Status-Chips führt er auf dieselbe gefilterte Liste (A-W3).
    case "kunde":
      if (route.herkunft !== undefined && isSupplierArea(route.herkunft)
        && route.originFilter !== undefined && route.originFilter.length > 0) {
        return { ziel: supplierAreaRoute(route.herkunft, route.originFilter), beschriftung: ZURUECK_BESCHRIFTUNG[route.herkunft] };
      }
      return route.herkunft ?? "kundenliste";
    case "kundeNeu": return "kundenliste";
    // Die unbekannte Adresse kennt ihren Ursprung nicht. Ihr eigener Knopf im Inhalt führt
    // zur Kundenliste; der Rückweg im Kopf sagt dasselbe, statt zu verschwinden.
    case "unbekannt": return "kundenliste";
  }
}

/**
 * Wohin ein Rückweg führen kann. Das sind die vier Herkünfte (die globalen Listen, aus
 * denen man in ein fremdes Ziel springt) plus die zwei Bildschirme, die eine Ansicht ohne
 * Herkunft unter sich hat: die Kundenliste und — seit Etappe 2, Aufgabe 1 — die Übersicht
 * der Verwaltungsbereiche.
 *
 * Der letzte Fall kam mit Aufgabe 2 dazu und ist der einzige, der nicht mit dem blossen
 * Namen auskommt: die OBJEKTLISTE eines Verwaltungsbereichs. Sie ist keine eigene Adresse
 * neben den anderen, sondern `/verwaltung/<bereich>` — also reist der Bereich mit, und mit
 * ihm seine Beschriftung aus VERWALTUNGS_BEREICHE.
 */
type Rueckziel =
  /** `schnittstellen` und `einstellungen` kamen mit Etappe 3 dazu — zwei weitere
   *  Bildschirme, die eine Ansicht ohne Herkunft unter sich hat: die Dienstliste und das
   *  Untermenü der Einstellungsgruppen. */
  | "kundenliste" | "verwaltung" | "schnittstellen" | "einstellungen" | Herkunft
  /**
   * Ein Rückweg auf einen Bildschirm, den kein Name allein trifft — die Objektliste eines
   * Verwaltungsbereichs (`/verwaltung/<bereich>`) und die Statuslisten der Objektbereiche
   * (`/angebote/<filter>`, `/auftraege/<filter>`, `/rechnungen/<filter>`). Sie sind keine
   * eigenen Adressnamen, sondern Zustände eines Namens; also reist die ganze ADRESSE mit,
   * samt allem, was an ihr hängt (der Filter, und in der Verwaltung die Herkunft, mit der
   * man hereinkam — sonst führte von der Objektliste kein Weg mehr in den Katalog zurück).
   *
   * Seit dem 07.09.2026 EINE Gestalt für beides statt zweier ähnlicher: Bis dahin gab es
   * je einen Fall für den Verwaltungsbereich und für den Rechnungsfilter, und mit den
   * Filtern von Angebot und Auftrag wären es vier geworden — vier Wege, dieselbe Sache zu
   * sagen („geh auf DIESE Adresse und nenne sie so").
   *
   * Die Beschriftung kommt mit, weil sie das ZIEL nennt und sich aus der Adresse nicht
   * errechnen lässt (deutsche Artikel; und ein Satz je Statusgruppe wäre eine zweite
   * Beschriftungstabelle für dieselbe Sache — „Zurück zu den Rechnungen" gilt für jede).
   */
  | { ziel: Route; beschriftung: string };

/**
 * Die Beschriftung des Rückwegs — sie NENNT das Ziel. „Zurück zur Liste" war die alte,
 * feste Ansage; sie stimmte nur, solange es eine einzige Liste gab, und ein Screenreader
 * las sie auch dann, wenn der Sprung woanders hinführte.
 *
 * Weil sie das Ziel nennt, muss sie mit ihm ALTERN: `artikel` hiess bis zum 07.09.2026
 * „Zurück zum Artikelkatalog" — der Katalog als eigene Ansicht ist mit dem Artikel-BEREICH
 * entfallen (siehe dort), also nannte der Satz eine Ansicht, die es nicht mehr gibt. Die
 * übrigen sieben Ziele wurden bei der Gelegenheit gegengelesen; ihre Wörter stehen alle
 * noch (Kundenliste, Verwaltungsbereiche, Dienste, Einstellungsgruppen).
 */
const ZURUECK_BESCHRIFTUNG: Record<Exclude<Rueckziel, object>, string> = {
  kundenliste: PARTNER_TEXT.backToList,
  angebote: "Zurück zu den Angeboten",
  auftraege: "Zurück zu den Aufträgen",
  artikel: "Zurück zu den Artikeln",
  rechnungen: "Zurück zu den Rechnungen",
  verwaltung: "Zurück zu den Verwaltungsbereichen",
  schnittstellen: "Zurück zu den Diensten",
  einstellungen: "Zurück zu den Einstellungsgruppen",
  liefervertraege: "Zurück zu den Lieferverträgen",
  eingangsrechnungen: "Zurück zu den Eingangsrechnungen",
  cockpit: COCKPIT_TEXT_WEB.backToArea,
  liquidity: LIQUIDITY_TEXT_WEB.toolbar.backToContent,
};

function zurueckBeschriftung(weg: Rueckziel): string {
  // Die Objektliste eines Verwaltungsbereichs bringt ihre Beschriftung selbst mit (aus
  // VERWALTUNGS_BEREICHE) — zehn Zeilen in der Tabelle oben wären eine zweite Quelle.
  return typeof weg === "string" ? ZURUECK_BESCHRIFTUNG[weg] : weg.beschriftung;
}

/**
 * Die Vorgabe der mehrfach wählbaren Chip-Leiste: alle vier Gruppen eingeschaltet.
 *
 * Als Modulkonstante und nicht als Aufruf im Zeichnen: Der Rechnungs-Bereich führt die
 * Auswahl in seinen Abhängigkeitslisten, und ein bei jedem Durchlauf neu gebautes Array
 * wäre dort jedes Mal eine neue Referenz.
 *
 * Angebote und Aufträge brauchen kein Gegenstück: Ihre Leiste ist einfach wählbar, und
 * „alle" ist dort schlicht `null` (kein Segment in der Adresse).
 */
const ALLE_RECHNUNGSFILTER = alleFilter(RECHNUNGS_FILTER);

/** Die Adresse hinter einem Rückweg. */
/** Die Adresse eines Lieferantenbereichs mit seinen Status-Chips (A-W3); ohne Chip die ganze Liste. */
function supplierAreaRoute(name: SupplierArea, filter: SupplierAreaFilter): Route {
  if (filter.length === 0) return { name };
  return name === "liefervertraege"
    ? { name, filter: filter.filter((f) => SUPPLIER_CONTRACT_FILTER.some((e) => e.id === f)) as typeof SUPPLIER_CONTRACT_FILTER[number]["id"][] }
    : { name, filter: filter.filter((f) => INCOMING_FILTER.some((e) => e.id === f)) as typeof INCOMING_FILTER[number]["id"][] };
}

function zurueckRoute(weg: Rueckziel): Route {
  // Die benannten Rückziele sind Adressen ohne Zusatzangaben; alles Übrige bringt seine
  // Adresse selbst mit (siehe den Typ oben).
  if (typeof weg !== "string") return weg.ziel;
  return weg === "kundenliste" ? { name: "kundenliste" } : { name: weg };
}

function alsFehler(ursprung: unknown): KundenFehler {
  if (ursprung instanceof ApiError) {
    // exactOptionalPropertyTypes: incidentId fehlt ganz statt explizit auf undefined zu stehen.
    return {
      message: ursprung.message,
      code: ursprung.code,
      ...(ursprung.incidentId !== undefined ? { incidentId: ursprung.incidentId } : {}),
    };
  }
  return { message: "Unerwarteter Fehler. Bitte die Seite neu laden." };
}

/**
 * Die Rollenfakten trägt nur der Leseweg der Akte (`GET /api/customers/:id`, Aufgabe 22); eine Schreibantwort (Stammdaten,
 * Konzern) behält die zuletzt gelesenen, sonst kippten die Reiter der Akte nach jedem Speichern auf „alle Rollen“.
 */
function withRoleFacts(neu: KundeAkteDaten, alt: KundeAkteDaten | null): KundeAkteDaten {
  return neu.roleFacts !== undefined || alt?.roleFacts === undefined || alt.id !== neu.id ? neu
    : { ...neu, roleFacts: alt.roleFacts };
}

function zurAnmeldung(): void {
  window.location.assign("/auth/login");
}

/**
 * Verdrahtet Sitzung, API-Client und den gefalteten Rahmen (MeSchale) aus @kundeninfo/ui.
 * Kundenkopf und Tab-Leiste liegen — wie in KundenAkte.tsx beschrieben — außerhalb des
 * Inhaltsbereichs; hier auf App-Ebene gilt dieselbe Regel für die ganze Anwendung: Liste,
 * Navigation und Kontext werden je einmal gebaut, MeSchale entscheidet nur über die Faltung.
 */
export function App(): ReactElement {
  const [sitzung, setSitzung] = useState<Sitzung | null>(null);
  /*
   * Die WELT dieser Instanz — eine SITZUNGSKONSTANTE (Spec „Umgebungskennzeichnung").
   * Sie wird EINMAL mit der Sitzung gesetzt und danach nie wieder angefasst: In welchem
   * System jemand arbeitet, ändert sich innerhalb einer Sitzung nicht. Bis die Antwort da
   * ist, gilt „test" — dieselbe ausfallsichere Richtung wie serverseitig; sichtbar wird
   * das ohnehin nie, weil bis dahin die Ladeseite steht und keine Kopfleiste.
   */
  const [umgebung, setUmgebung] = useState<UmgebungsAnzeige>(
    { klasse: "test", instanz: null, konflikt: false });
  /** Der Zuschnitt aus dem Hostnamen (Spec „Belegdesign als eigenständige ME-Anwendung“) —
   *  wie die Umgebung eine Sitzungskonstante. Kosmetik, kein Sicherheitsmerkmal: Er filtert
   *  nur die Darstellung; Rechte prüft der Server (siehe zuschnitt.ts). */
  const [zuschnittSitzung, setZuschnittSitzung] = useState<Zuschnitt>("voll");
  const [sitzungFehler, setSitzungFehler] = useState<KundenFehler | null>(null);
  const [ladeSitzung, setLadeSitzung] = useState(true);

  // Eine über den Unternehmenswechsel mitgenommene Suche (R22): im Initialisierer lesen, im Effekt
  // löschen — StrictMode ruft Initialisierer doppelt, ein Löschen dort verlöre die Suche.
  const [search, setSearch] = useState(() => leseKundensuche() ?? "");
  useEffect(() => { vergissKundensuche(); }, []);
  const [items, setItems] = useState<KundeListeEintrag[]>([]);
  /** Die Rollen-Chips der Geschäftspartnerliste (Festlegung C9): im Browser gemerkt, ausdrücklich an den Server. */
  const [rollenWahl, setRollenWahl] = useState<RollenWahl>(leseRollenWahl);
  const chooseRoles = useCallback((w: RollenWahl): void => { setRollenWahl(w); merkeRollenWahl(w); }, []);
  const [weitereTreffer, setWeitereTreffer] = useState<WeitererTreffer[]>([]);
  const [listeLaedt, setListeLaedt] = useState(false);
  const [listeFehler, setListeFehler] = useState<KundenFehler | null>(null);
  /** Hochgezählt, wenn die Liste einen Datensatz noch nicht kennen kann — nach dem
   *  Anlegen eines Kunden (Aufgabe 17) lädt der Listen-Effekt darüber neu. */
  const [listeVersion, setListeVersion] = useState(0);

  /*
   * Aufgabe 15: Auswahl und Tab leben nicht mehr in eigenem Zustand, sondern in der
   * Adresse (Browserverlauf). Damit ist jeder Bildschirm verlinkbar und die
   * Zurück-Taste bewegt sich innerhalb der Anwendung, statt sie zu verlassen.
   * Der dritte Rückgabewert wandert zum Stammdaten-Tab (Aufgabe 16): der registriert
   * bei ungespeicherten Änderungen eine Nachfrage, die jede Navigation anhält.
   */
  const [route, navigiere, registriereVerlassenWaechter, fragVerlassen] = useRoute();
  const selectedId = route.name === "kunde" ? route.id : null;
  const activeTab: AktenTabId = route.name === "kunde" ? route.tab : "uebersicht";

  /*
   * Die zuletzt geöffnete Zeile je Liste — nur für den Telefon-Einspalter (Hausregel „Scroll
   * bei Neuzeichnen", Review-Fokus 4 der ME-Etappe 2). Dort zeigt der Rahmen Liste ODER
   * Objekt; der Rückweg führt auf die Listenadresse OHNE Kennung, und die Auswahl, die aus
   * der Adresse kommt, wäre damit fort. Die Liste markiert deshalb am Telefon die zuletzt
   * geöffnete Zeile, bis eine andere geöffnet wird (den Scrollstand hält die Schale, die die
   * Liste dort nur verbirgt). Auf iPad und Desktop steht das Objekt neben der Liste — dort
   * gilt allein die Adresse.
   *
   * Ein Ref, kein Zustand: Der Wert folgt der Adresse im selben Durchlauf und löst selbst
   * nichts aus; das Fortschreiben ist idempotent.
   */
  const einspalter = useEinspalter();
  const phone = useTelefon();
  const zuletztGeoeffnet = useRef<Partial<Record<
    "kunde" | "angebote" | "auftraege" | "artikel" | "rechnungen", string>>>({});
  if ((route.name === "kunde" || route.name === "angebote" || route.name === "auftraege"
       || route.name === "artikel" || route.name === "rechnungen") && route.id !== undefined) {
    zuletztGeoeffnet.current[route.name] = route.id;
  }
  const markiert = (ziel: keyof typeof zuletztGeoeffnet.current): string | null =>
    einspalter ? (zuletztGeoeffnet.current[ziel] ?? null) : null;

  const [akte, setAkte] = useState<KundeAkteDaten | null>(null);
  const [akteFehler, setAkteFehler] = useState<KundenFehler | null>(null);
  /**
   * Hochgezählt, wenn die Akte einen Stand zeigt, den sie nicht selbst kennen kann — heute
   * nach einer Änderung an den ADRESSEN (Etappe 1): Die Hauptanschrift im Kopf ist aus der
   * Hauptadresse ABGELEITET und entsteht im Server, nicht hier.
   *
   * Ein Zähler und kein `setAkte(kunde)` aus dem Reiter heraus: Derselbe Kundensatz wäre
   * dieselbe Referenz, und React verwürfe das `setState` (Object.is) — der Kopf bliebe
   * wortlos alt stehen. Dieselbe Naht wie `listeVersion` eine Bildschirmhöhe weiter oben.
   */
  const [akteVersion, setAkteVersion] = useState(0);
  const akteNeuLaden = useCallback(() => setAkteVersion((v) => v + 1), []);
  /*
   * Der Randzustand wird beim ersten Zeichnen aus dem Browser geholt, damit die Ränder
   * sofort richtig stehen und nicht sichtbar nachrücken, wenn die Sitzung eintrifft.
   * Gemerkt wird nur, solange rememberPanels an ist (siehe die zwei Effekte unten) — liegt
   * ein Wert vor, war der Schalter zuletzt an. Steht er inzwischen auf aus (z. B. auf einem
   * anderen Gerät umgelegt, der Schalter liegt ja in der Datenbank), räumt der Effekt
   * unten auf. Ohne brauchbaren Wert gilt der Standard (siehe panelSpeicher.ts).
   */
  const [panels, setPanels] = useState<PanelState>(() => lesePanels() ?? STANDARD_PANELS);
  const [fensterWahl, setFensterWahl] = useState(leseFensterUnternehmen);

  /**
   * Aufgabe 11: Der Verwaltungsbereich gehört ADMIN. Der Server weist alle Routen unter
   * /api/verwaltung/* für alle anderen mit 403 ab (fail closed) — dieser Wächter schützt
   * also keine Daten, er verhindert nur, dass jemand über ein Lesezeichen oder einen
   * geteilten Link auf einem Bildschirm landet, der ihm ausschliesslich Fehler zeigt.
   * Deshalb Umleitung MIT Meldung: Ein wortloser Sprung sähe aus wie ein Fehler.
   */
  const [verwaltungMeldung, setVerwaltungMeldung] = useState<string | null>(null);

  const [einstellungenOffen, setEinstellungenOffen] = useState(false);
  const [einstellungenFehler, setEinstellungenFehler] = useState<string | null>(null);
  /**
   * „Neuer Geschäftspartner“ (Entscheid 524, Hausregel „Modal-Bearbeitung“): Das Modalpanel liegt wie das Anlegen der
   * Tickets als ZUSTAND über der Ansicht, an deren Adresse es geöffnet wurde — Liste und Akte darunter bleiben stehen,
   * nach dem Schließen steht man wieder dort. Gemerkt wird die Adresse: Wechselt sie (Zurück-Taste, anderer Bereich),
   * fällt das Panel mit ihr (Muster „Zustand bei geänderter Eingabe zurücksetzen“, im selben Zeichnen). Der
   * Direktaufruf /kunden/neu (`kundeNeu`) öffnet es ohne diesen Zustand; sein Schließen führt auf die Kundenliste.
   */
  const [partnerNeuAn, setPartnerNeuAn] = useState<string | null>(null);
  if (partnerNeuAn !== null && partnerNeuAn !== routeToPath(route)) setPartnerNeuAn(null);
  const partnerNeuOffen = route.name === "kundeNeu" || partnerNeuAn !== null;
  /**
   * Kleinpaket „Service Worker: Hinweis ‚Neue Version'": registerSw.ts löst "ki:neue-
   * version" aus, sobald ein neuer Service Worker die Seite übernommen hat
   * (controllerchange, siehe registerSw.ts). Ohne diesen Hinweis sah ein offen gelassener
   * Tab nach einem Deploy nur noch weiße Seite bzw. Quelltext — der alte Worker ist weg,
   * der neue bedient Anfragen auf Dateien anders, und niemand sagte der Seite, dass sie
   * neu laden sollte.
   */
  const [neueVersionBereit, setNeueVersionBereit] = useState(false);
  useEffect(() => {
    const melde = (): void => setNeueVersionBereit(true);
    window.addEventListener("ki:neue-version", melde);
    return () => window.removeEventListener("ki:neue-version", melde);
  }, []);
  const einstellungenRef = useRef<HTMLDivElement>(null);
  // Fixrunde 1, Befund 2: Fokus setzen, Tabulator fangen, Escape schließt, Fokus beim
  // Schließen zurückgeben — als wiederverwendbarer Baustein aus @kundeninfo/ui, nicht
  // als Einzellösung hier (siehe packages/ui/src/overlay/useFocusTrap.ts).
  useFocusTrap(einstellungenOffen, einstellungenRef, () => setEinstellungenOffen(false));

  // Sitzung einmalig laden. Ohne gültige Anmeldung (401) geht es direkt zu Keycloak weiter —
  // die Oberfläche selbst kennt keinen eigenen Anmeldebildschirm, das übernimmt /auth/login.
  useEffect(() => {
    let abgebrochen = false;
    client.getSession()
      .then((antwort) => {
        if (abgebrochen) return;
        if (antwort.user === undefined) { zurAnmeldung(); return; }
        applyTheme(antwort.user.colorScheme ?? "SYSTEM", antwort.user.density ?? "COMPACT",
          schriftAus(antwort.user.fontScale ?? "AUTO", geraetErmitteln()));
        // Steht der Schalter aus, gilt der Standard — auch dann, wenn beim ersten Zeichnen
        // (vor dieser Antwort) noch ein gemerkter Wert übernommen wurde, etwa weil er auf
        // einem anderen Gerät gemerkt und der Schalter dort später umgelegt wurde. Der
        // Wechsel ist unsichtbar: Bis hierher zeigt die Anwendung ihre Ladeseite, nicht
        // den Rahmen. Verworfen wird der Wert vom Effekt weiter unten.
        if (antwort.user.rememberPanels === false) setPanels(STANDARD_PANELS);
        setUmgebung(umgebungAusSitzung(antwort));
        setZuschnittSitzung(zuschnittAusSitzung(antwort));
        const neu = { user: antwort.user, companies: antwort.companies ?? [],
                      aircallCompanyId: antwort.aircallCompanyId ?? null };
        // Der Tab merkt sich beim ersten Sitzungsstand sein Unternehmen (Dirk 391, R3) — ein späterer Wechsel in
        // einem anderen Tab ändert nur die Vorgabe für neue Tabs.
        setFensterWahl(halteFensterUnternehmen(neu, fensterWahl));
        setSitzung(neu);
        setLadeSitzung(false);
      })
      .catch((ursprung) => {
        if (abgebrochen) return;
        if (ursprung instanceof ApiError && ursprung.status === 401) { zurAnmeldung(); return; }
        setSitzungFehler(alsFehler(ursprung));
        setLadeSitzung(false);
      });
    return () => { abgebrochen = true; };
  }, []);

  /*
   * Aktionsfarbe und Wortmarke folgen dem ARBEITSUNTERNEHMEN dieses Fensters (ME-Etappe 1,
   * Aufgabe 6; Mandantentrennung R16): Wahl des Tabs, sonst die gespeicherte Vorgabe, sonst
   * — nur als Rückfall — das Heimatunternehmen, dann ME (`arbeitsunternehmen` in session.ts).
   * Bis dahin folgte die Farbe dem Heimatunternehmen (Spec Unternehmens-Verwaltung).
   *
   * Mit dem Kürzel: Die drei bekannten Unternehmen (BS/ME/PA) färbt me-design selbst über
   * `data-unternehmen`, ohne eigenen Stilblock; nur ein unbekanntes Kürzel bekommt die aus
   * seinem `colorHex` abgeleitete Übersteuerung der Tokens (@kundeninfo/ui), anwendungsweit
   * und ohne Einzelstellen-Färbung. Ohne jedes Unternehmen bleibt es bei den Vorgabefarben.
   *
   * Als eigener Effekt an Farbe und Kürzel, nicht als Zeile im Sitzungs-Effekt oben: So folgt
   * sie auch einer späteren Änderung (Wechsel des Arbeitsunternehmens, eine umgestellte
   * Heimat ohne eigene Wahl), und die Abhängigkeit ist genau die eine Tatsache, die zählt.
   */
  const arbeit = sitzung === null ? null : arbeitsunternehmen(sitzung, fensterWahl);
  // Der Kopf jeder Anfrage folgt dem abgeleiteten aktiven Unternehmen (R2) — idempotent beim Zeichnen.
  anfrageUnternehmen.id = arbeit?.id ?? fensterWahl;
  const arbeitsfarbe = arbeit?.colorHex ?? null;
  const arbeitskuerzel = arbeit?.shortCode ?? null;
  /** Dieselbe Wahl als Kennung — Vorbelegung beim Anlegen; die Verwaltungsbereiche (Vorlagen,
   *  Textbausteine, Layout, Nummernkreise, Artikel) zeigen nur dieses Unternehmen (E364). Hier
   *  oben, weil die Bereichs-Hooks sie schon vor dem Eintreffen der Sitzung brauchen. `null` =
   *  kein aktives Unternehmen: Die Bereiche zeigen leere Listen und fragen den Server gar nicht —
   *  nie „alle“ (Spec R6: ohne aktives Unternehmen ist die Sicht leer). */
  const arbeitsunternehmenId = arbeit?.id ?? null;
  useEffect(() => { setzeAktionsfarben(arbeitsfarbe, arbeitskuerzel); },
    [arbeitsfarbe, arbeitskuerzel]);

  /*
   * Reitertitel und Farbe der Browserleiste folgen der Welt (Spec: Präfix „[TEST] " bzw.
   * „[ENTW] ", `theme-color` gleich der Kopfleistenfarbe).
   *
   * Warum HIER und nicht in index.html: Das gebaute Auslieferungspaket ist umgebungsblind
   * — dasselbe dist/ läuft auf office, auf test01 und am Arbeitsplatz. Manifest, Favicon
   * und Startbildschirm-Icon kommen deshalb vom Dienst (siehe index.html); Titel und
   * `theme-color` sind Angaben IM Dokument und lassen sich nur von hier aus setzen.
   *
   * Abhängig allein von der Umgebung, nicht von der Sitzung: Die Zeile soll genau einmal
   * laufen und nicht bei jeder Änderung an Benutzer oder Unternehmen erneut.
   */
  const istAdmin = sitzung?.user.role === "ADMIN";
  /** Der WIRKSAME Zuschnitt — für Nicht-Administratoren immer „voll" (siehe zuschnitt.ts). */
  const zuschnitt = wirksamerZuschnitt(zuschnittSitzung, istAdmin);
  /*
   * PUSH ABSTIMMEN (F105, Review Task 12): Hat dieser Browser schon ein Push-Abo, wird es nach
   * jeder hergestellten Sitzung einmal still neu beim Dienst angemeldet (Upsert per Endpunkt).
   * Damit wandert das Gerät beim Nutzerwechsel zum jetzt Angemeldeten — sonst bekäme der
   * Vorgänger weiter Mitteilungen auf diesem Gerät —, und ein verwaistes Abo (anderswo
   * entfernt, gescheitertes `pushsubscriptionchange`) heilt sich. Fragt nie nach einer
   * Erlaubnis, legt nie ein Abo an; Fehler bleiben folgenlos. Nur im vollen Zuschnitt (Tickets).
   */
  const benutzerId = sitzung?.user.id ?? null;
  useEffect(() => {
    if (benutzerId === null || zuschnitt !== "voll") return;
    void pushAbstimmen(client, browserUmgebung()).catch(() => undefined);
  }, [benutzerId, zuschnitt]);

  // Bei offener Akte „Akte ‹Firma›“ im Reitertitel (Spec „Wortwahl“, Lieferanten Aufgabe 22).
  const akteTitel = route.name === "kunde" && akte !== null && akte.id === route.id ? akte.companyName : null;
  useEffect(() => {
    document.title = windowTitle(titelPraefix(umgebung), anwendungsname(zuschnitt), akteTitel);
    const marke = document.querySelector('meta[name="theme-color"]');
    marke?.setAttribute("content", UMGEBUNGSFARBEN[umgebung.klasse].grund);
  }, [umgebung, zuschnitt, akteTitel]);

  /*
   * EINSTIEG im Zuschnitt „belegdesign": Das Cockpit ist die Vorgabe-Route (`/`, K26), der
   * Zuschnitt kennt es nicht; es führt wie die Kundenliste direkt in den Belegdesigner. Ersetzen statt Anhängen, damit die
   * Zurück-Taste nicht in eine Schleife läuft. Das Arbeitsunternehmen belegt der
   * Layout-Bereich selbst vor (views/verwaltung/layout.tsx). Kosmetik, kein
   * Sicherheitsmerkmal — eine eingetippte Kundenadresse bleibt erreichbar, soweit die
   * Rolle es hergibt.
   */
  const entersDesigner = zuschnitt === "belegdesign" && (route.name === "cockpit" || route.name === "kundenliste");
  useEffect(() => {
    if (sitzung === null || !entersDesigner) return;
    navigiere(zielRoute("belegdesign", PURCHASING_TAB_DEFAULT), true);
  }, [sitzung, entersDesigner, navigiere]);

  const willVerwaltung = route.name === "verwaltung";
  useEffect(() => {
    if (sitzung === null || !willVerwaltung || istAdmin) return;
    setVerwaltungMeldung(PARTNER_TEXT.adminAreaRedirect);
    // Ersetzen statt Anhängen: Sonst führte die Zurück-Taste geradewegs wieder auf die
    // Adresse, von der eben umgeleitet wurde.
    navigiere({ name: "kundenliste" }, true);
  }, [sitzung, willVerwaltung, istAdmin, navigiere]);

  // Liste neu laden, sobald die Sitzung steht oder sich die (bereits verzögert gemeldete)
  // Suche ändert.
  //
  // Die ROLLEN-CHIPS gelten nur der Geschäftspartnerliste (Spec C9: „Die Geschäftspartnerliste schickt ihre Chipwahl
  // immer ausdrücklich“, Aufgabe 22). Dieselbe Liste dient den Bereichen Angebote und Aufträge als Kundenwahl beim
  // Anlegen — dort bleibt es bei der Vorgabe des Servers (Kunden und Interessenten), sonst böte eine Chipwahl
  // „Lieferanten“ dort Lieferanten als Empfänger eines Angebots an.
  //
  // Gilt nur auf Routen, die die Liste zeigen (Spalte „Kunden“, Kundenwahl in Angebote/Aufträge): Anderswo bleibt die
  // zuletzt gültige Anfrage stehen, sonst lüde jeder Wechsel in einen Bereich ohne Liste sie umsonst neu (A-K10).
  const showsList = spalteFuer(route) === "kunden" || route.name === "angebote" || route.name === "auftraege";
  const lastRoleQuery = useRef<{ role: RollenWahl["roles"]; includeFormer: boolean } | null>(null);
  if (showsList) {
    lastRoleQuery.current = spalteFuer(route) === "kunden"
      ? { role: rollenWahl.roles, includeFormer: rollenWahl.includeFormer } : null;
  }
  const roleQuery = lastRoleQuery.current;
  const roleKey = JSON.stringify(roleQuery);
  useEffect(() => {
    if (sitzung === null) return;
    let abgebrochen = false;
    setListeLaedt(true);
    setListeFehler(null);
    client.getCustomers({ ...(search === "" ? {} : { search }), limit: 100, ...(roleQuery ?? {}) })
      .then((antwort) => {
        if (abgebrochen) return;
        setItems(antwort.items);
        setWeitereTreffer(antwort.otherCompanyHits);
        setListeLaedt(false);
      })
      .catch((ursprung) => {
        if (abgebrochen) return;
        setListeFehler(alsFehler(ursprung));
        setListeLaedt(false);
      });
    return () => { abgebrochen = true; };
    // `roleKey` trägt die Wahl als Wert (die Anfrage selbst ist je Zeichnen ein neues Objekt).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitzung, search, listeVersion, roleKey]);

  // Akte neu laden, sobald die Auswahl wechselt. Bewusst NICHT vom Tab abhängig:
  // Ein Tabwechsel tauscht nur den Inhalt der Akte, er lädt sie nicht neu.
  // `akteVersion` wirft denselben Abruf erneut an, ohne dass die Auswahl wechselt.
  const gezeigteId = useRef<string | null>(null);
  useEffect(() => {
    if (sitzung === null || selectedId === null) {
      gezeigteId.current = null;
      setAkte(null); setAkteFehler(null); return;
    }
    let abgebrochen = false;
    // Geleert wird nur beim WECHSEL des Kunden: Beim blossen Nachladen (akteVersion) stünde
    // die Akte sonst für einen Augenblick leer da — der offene Reiter fiele mitsamt seinem
    // Zustand weg, und das ausgerechnet als Quittung für eine gelungene Änderung.
    if (gezeigteId.current !== selectedId) {
      gezeigteId.current = selectedId;
      setAkte(null);
    }
    setAkteFehler(null);
    client.getCustomer(selectedId)
      .then((k) => { if (!abgebrochen) setAkte(k); })
      .catch((ursprung) => { if (!abgebrochen) setAkteFehler(alsFehler(ursprung)); });
    return () => { abgebrochen = true; };
  }, [sitzung, selectedId, akteVersion]);

  /*
   * Ein REITER in der Adresse, den die Akte nicht zeichnet (Lieferanten Aufgabe 22, Fix-Runde 1 K8; Final-Fixrunde A-K7):
   * ein Lieferantenreiter ohne Lieferantensicht — schon bevor die Akte da ist, damit nichts geladen wird — und, sobald sie
   * da ist, jeder Reiter außerhalb der Rollen der Firma (etwa „Lieferverträge“ als ADMIN bei einem reinen Kunden). Die
   * Adresse soll ihn nicht behaupten — ersetzt (kein Verlaufseintrag, sonst führte die Zurück-Taste wieder hierher) durch
   * die Übersicht derselben Akte, die Herkunft bleibt.
   */
  const hiddenTab = route.name === "kunde" && sitzung !== null
    && ((!seesSupplierData(sitzung.user.role) && SUPPLIER_TABS.includes(route.tab))
      || (akte !== null && akte.id === route.id && !visibleTabs(AKTEN_TABS, { roleFacts: akte.roleFacts ?? ALL_ROLES,
        supplierView: seesSupplierData(sitzung.user.role) }).some((t) => t.id === route.tab)));
  const lockedTabRedirect: Route | null = route.name === "kunde" && hiddenTab
    ? { name: "kunde", id: route.id, tab: "uebersicht", ...(route.herkunft === undefined ? {} : { herkunft: route.herkunft }) }
    : null;
  const lockedTabPath = lockedTabRedirect === null ? null : routeToPath(lockedTabRedirect);
  useEffect(() => {
    if (lockedTabRedirect !== null) navigiere(lockedTabRedirect, true);
    // Der Pfad trägt das Ziel als Wert (das Objekt ist je Zeichnen neu).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedTabPath, navigiere]);

  /*
   * Aufgabe 1: `rememberPanels` wirksam machen. Der Schalter selbst steht am Benutzer in der
   * Datenbank, der Randzustand im Browser (siehe panelSpeicher.ts).
   *
   * Ist der Schalter an, wird jede Änderung am Randzustand gemerkt — auch die erste, damit
   * ein Neustart gleich nach dem Einschalten schon das Richtige wiederherstellt. Ist er aus,
   * wird nichts gemerkt UND ein vorhandener Wert verworfen: Er könnte von früher stammen
   * oder von einem anderen Gerät her noch liegen, und beim nächsten Einschalten würde er
   * sonst einen längst überholten Zustand wiederbeleben.
   */
  const sitzungSteht = sitzung !== null;
  const merken = sitzung?.user.rememberPanels ?? true;
  useEffect(() => {
    if (!sitzungSteht) return;
    if (merken) merkePanels(panels);
    else vergissPanels();
    // Bewusst NICHT von `sitzung` abhängig, sondern nur von den zwei Tatsachen, die hier
    // zählen — sonst liefe der Effekt bei jeder anderen Einstellungsänderung mit.
  }, [sitzungSteht, merken, panels]);

  /** Welche Liste in der Spalte des Rahmens steht — siehe `spalteFuer` oben. */
  const spalte = spalteFuer(route);

  /*
   * Die Angebot→Auftrag-Naht, BEREICHSINTERN (Ruling des Controllers vom 06.09.2026).
   *
   * Bis Aufgabe 3 lief sie über die Kundenakte: Der Angebots-Bereich sprang in den
   * Angebote-Reiter, dort stand derselbe Knopf noch einmal, und die Akte hielt die
   * Vorbelegung. Mit dem Auftrags-BEREICH gibt es einen Ort, an dem der neue Auftrag
   * hingehört — deshalb reist die Vorbelegung jetzt von Bereich zu Bereich.
   *
   * Sie steht in einem Zustand hier oben und NICHT in der Adresse, aus demselben Grund wie
   * `vorgangId` (siehe router.ts): Eine halb ausgefüllte Maske ist kein Bildschirm, ein
   * Lesezeichen darauf wäre sinnlos. Verbraucht wird sie sofort beim Aufnehmen
   * (`onAusAngebotAufgenommen`) — sie soll genau EINEN Sprung überleben.
   *
   * Die Kundenakte bleibt davon unberührt: Ihr eigener Weg (Angebote-Reiter →
   * Aufträge-Reiter) besteht unverändert weiter, samt seiner Vorbelegung in KundenAkte.tsx.
   */
  const [auftragAusAngebot, setAuftragAusAngebot] = useState<AuftragAusAngebot | null>(null);
  /** Stabil, weil der Auftrags-Bereich sie in einer Abhängigkeitsliste führt: Ein bei jedem
   *  Zeichnen neu gebauter Rückruf liesse seinen Aufnahme-Effekt unnötig mitlaufen. */
  const auftragAusAngebotVerbraucht = useCallback(() => setAuftragAusAngebot(null), []);

  /*
   * Dasselbe eine Stufe weiter: die Auftrag→Rechnung-Naht, seit Aufgabe 5b ebenfalls
   * BEREICHSINTERN (Entscheid Dirk 06.09.2026).
   *
   * Bis dahin führte sie über die Kundenakte — es gab keinen Rechnungs-Bereich, in den der
   * frische Entwurf gehört hätte. Jetzt gibt es ihn: Der Auftrags-Bereich leitet die
   * Rechnung ab und meldet sie hier, die Adresse wechselt auf `/rechnungen/<neueId>`, und
   * der Rechnungs-Bereich nimmt sie beim Betreten auf. Der KUNDE reist mit, weil eine
   * Rechnung nur über ihn gelesen werden kann (siehe den Kopf von BereichRechnungen.tsx).
   *
   * Die Kundenakte bleibt davon unberührt: Ihr eigener Weg (Aufträge-Reiter →
   * Rechnungen-Reiter, `oeffneRechnungId`) besteht unverändert weiter.
   */
  const [rechnungAusAuftrag, setRechnungAusAuftrag] = useState<RechnungAusAuftrag | null>(
    null);
  const rechnungAusAuftragVerbraucht = useCallback(
    () => setRechnungAusAuftrag(null), []);

  /*
   * Der Angebots-BEREICH (Etappe 1, Aufgabe 3). Er füllt ZWEI Bereiche des Rahmens —
   * Spalte und Detail — und hält den gemeinsamen Zustand selbst; deshalb liefert er seine
   * beiden Stücke als Hook (siehe views/BereichAngebote.tsx).
   *
   * Er steht hier oben, VOR den Rückgaben für Ladezustand und Sitzungsfehler: Ein Hook
   * darf nicht hinter einer Bedingung stehen. Dass er an jeder anderen Adresse nichts tut,
   * regelt `aktiv` — sonst liefe seine Suche auf jedem Bildschirm mit.
   */
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ eines Bereichs (Aufgabe 25): in den Reiter „Dateien“ der Akte, in
   *  diesem Ordner — mit der Herkunft des Bereichs für den Rückweg (wie der Verweis „Zur Akte ‹Kunde›“). */
  const toFolder = (herkunft: Herkunft) => (kundeId: string, folderId: string): void =>
    navigiere({ name: "kunde", id: kundeId, tab: "dateien", folderId, herkunft });
  /*
   * Finanzen › Liquidität (Empfehlung 591): füllt wie die Objektbereiche zwei Bereiche des Rahmens und hält den Zustand selbst.
   * Ohne Lieferantensicht lädt er nichts (`areaLocked`). Ein Sprung bleibt in der Geschichte und führt über die Herkunft zurück.
   */
  const liquidityArea = useLiquidityArea({
    active: route.name === "liquidity" && sitzung !== null && !areaLocked("liquidity", sitzung.user.role),
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    activeCompanyId: arbeitsunternehmenId,
    userId: sitzung?.user.id,
    registriereVerlassenWaechter,
  });
  const angebotsFilter = route.name === "angebote" ? (route.filter ?? null) : null;
  const angeboteBereich = useBereichAngebote({
    // Der Riegel hängt jetzt an der ADRESSE und nicht mehr am Spalteninhalt: Seit dem
    // 07.09.2026 hat der Bereich keine Spalte mehr, über die er sich erkennen liesse.
    aktiv: route.name === "angebote",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    arbeitsunternehmenId,
    /* Der Statusfilter aus der Adresse — `null` heisst „alle" (siehe bereichsfilter.ts).
       Die Chip-Leiste zeichnet daraus ihren Zustand; ein zweiter Zustand daneben könnte
       mit der Adresse auseinanderlaufen. */
    filter: angebotsFilter,
    angebotId: route.name === "angebote" ? (route.id ?? null) : null,
    markiertId: markiert("angebote"),
    // Die bestehende Kunden-Suchauswahl, unverändert wiederverwendet: dieselbe Liste,
    // derselbe Suchtext, dieselbe Abfrage — der Bereich zeichnet sie nur an einer anderen
    // Stelle (als ersten Schritt des Anlegens).
    kunden: {
      items, loading: listeLaedt, error: listeFehler, search, onSearch: setSearch,
    },
    /* Ein Objekt aufschlagen bzw. zuklappen — der Filter BLEIBT: Die Zeile, die man
       anklickt, steht in der gefilterten Liste, und ein Sprung auf „alle" nähme sie einem
       unter den Fingern weg. */
    onOeffnen: (angebotId) => navigiere({
      name: "angebote",
      ...(angebotsFilter === null ? {} : { filter: angebotsFilter }),
      ...(angebotId === null ? {} : { id: angebotId }),
    }),
    /* Ein Filterwechsel klappt das offene Objekt zu: Es muss in der neuen Liste nicht
       stehen, und `/angebote/abgelehnt/<id>` über einer Liste ohne diese Zeile wäre eine
       Adresse, die etwas anderes behauptet, als zu sehen ist. */
    onFilter: (filter: AngebotsFilter | null) => navigiere({
      name: "angebote", ...(filter === null ? {} : { filter }),
    }),
    onOrdnerOeffnen: toFolder("angebote"),
    /*
     * Die Angebot→Auftrag-Naht bleibt unter den BEREICHEN (Ruling des Controllers vom
     * 06.09.2026): Die Vorbelegung wird hier abgelegt, die Adresse wechselt auf
     * `/auftraege`, und der Auftrags-Bereich nimmt sie beim Betreten auf. Nach dem Anlegen
     * steht der neue Auftrag unter `/auftraege/<neueId>` offen — dort, wo er hingehört,
     * statt in einer Akte, die man für einen einzigen Vorgang aufschlagen müsste.
     *
     * `kundeName` kommt aus der Trefferzeile des Angebots-Bereichs mit: Der Auftrags-
     * Bereich überspringt seine Kundenwahl und braucht den Namen für Überschrift und
     * Platzhalter-Vorschau, ohne den Kunden dafür zu laden.
     */
    onAuftragAusAngebot: (kundeId, kundeName, a) => {
      setAuftragAusAngebot({
        kundeId,
        kundeName,
        vorbelegung: {
          offerId: a.id,
          companyId: a.companyId,
          title: a.title,
          netAmount: a.netAmount,
          contactId: a.contactId,
          responsibleUserId: a.responsibleUserId,
          periodBasis: a.periodBasis,
        },
      });
      navigiere({ name: "auftraege" });
    },
    // Bei offener Bearbeitung fragt jede Navigation nach — dasselbe Hausmuster wie am
    // Stammdaten-Reiter, und derselbe Grund: Ein Rückweg darf Eingaben nicht stumm
    // verwerfen.
    registriereVerlassenWaechter,
  });

  /*
   * Der Auftrags-BEREICH (Etappe 1, Aufgabe 4) — dieselbe Bauart wie der Angebots-Bereich
   * darüber und aus denselben Gründen: zwei Stücke für den Rahmen, gemeinsamer Zustand im
   * Hook, `aktiv` als Riegel gegen mitlaufende Suchen an fremden Adressen.
   */
  const auftragsFilter = route.name === "auftraege" ? (route.filter ?? null) : null;
  const auftraegeBereich = useBereichAuftraege({
    aktiv: route.name === "auftraege",
    client,
    // Lösen von Abrechnungsquellen an nicht aktiven Aufträgen: nur ADMIN (Entscheid Dirk
    // 29.09.2026, Punkt 7).
    istAdmin,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    arbeitsunternehmenId,
    filter: auftragsFilter,
    auftragId: route.name === "auftraege" ? (route.id ?? null) : null,
    markiertId: markiert("auftraege"),
    kunden: {
      items, loading: listeLaedt, error: listeFehler, search, onSearch: setSearch,
    },
    // Das Ende der bereichsinternen Angebot→Auftrag-Naht (siehe oben).
    ausAngebot: auftragAusAngebot,
    onAusAngebotAufgenommen: auftragAusAngebotVerbraucht,
    onOeffnen: (auftragId) => navigiere({
      name: "auftraege",
      ...(auftragsFilter === null ? {} : { filter: auftragsFilter }),
      ...(auftragId === null ? {} : { id: auftragId }),
    }),
    onFilter: (filter: AuftragsFilter | null) => navigiere({
      name: "auftraege", ...(filter === null ? {} : { filter }),
    }),
    onOrdnerOeffnen: toFolder("auftraege"),
    /*
     * Die Auftrag→Rechnung-Naht — seit Aufgabe 5b BEREICHSINTERN (siehe oben): Der frische
     * Entwurf wird hier abgelegt, die Adresse wechselt auf `/rechnungen/<neueId>`, und der
     * Rechnungs-Bereich nimmt ihn beim Betreten auf. `herkunft` sagt dem Rückweg auf dem
     * iPhone, dass dahinter die Aufträge stehen und nicht die Kundenliste.
     *
     * Der Umweg über die Kundenakte entfällt damit — er war ausdrücklich der Bestandsweg
     * „bis es einen Rechnungs-Bereich gibt". Die Akte selbst bleibt unangetastet.
     */
    onRechnungAusAuftrag: (kundeId, rechnung) => {
      setRechnungAusAuftrag({ kundeId, rechnung });
      navigiere({ name: "rechnungen", id: rechnung.id, herkunft: "auftraege" });
    },
    registriereVerlassenWaechter,
  });

  /*
   * Der Artikel-BEREICH (Etappe 1, Aufgabe 5) — dieselbe Bauart wie die beiden darüber,
   * nur kundenlos: kein Verweis zur Akte, keine Kundenauswahl, kein Anlegen (das bleibt der
   * Verwaltung). Er löst die bisherige Katalog-Ansicht ab; die Adresse `/artikel` bleibt.
   *
   * Auch KEIN Verlassen-Wächter: Der einzige Bearbeitungszustand ist der Änderungsdialog
   * des Bausteins, und der bleibt in dieser Aufgabe unangetastet (Begründung im Kopf von
   * views/BereichArtikel.tsx).
   */
  const artikelBereich = useBereichArtikel({
    aktiv: route.name === "artikel",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    arbeitsunternehmenId,
    artikelId: route.name === "artikel" ? (route.id ?? null) : null,
    markiertId: markiert("artikel"),
    istAdmin,
    onOeffnen: (artikelId) => navigiere(
      { name: "artikel", ...(artikelId === null ? {} : { id: artikelId }) }),
    // Der Verweis in die Pflege steht nur ADMINs offen — für alle anderen ist die
    // Verwaltung mit 403 verschlossen (der Server prüft das), ein Verweis dorthin führte
    // sie nur in eine sichere Sackgasse. `herkunft` bringt den Rückweg auf dem iPhone
    // wieder hierher statt zur Kundenliste.
    onPflege: () => navigiere({ name: "verwaltung", bereich: "artikel",
                                herkunft: "artikel" }),
  });

  /*
   * Der Rechnungs-BEREICH — dieselbe Bauart wie die drei darüber, mit zwei Unterschieden,
   * die beide daraus folgen, dass eine Rechnung ein BELEG ist:
   *
   *  - Es gibt KEIN Anlegen (Rechnungen entstehen am Auftrag bzw. als Korrektur).
   *  - Seine Spalte hat eine Stufe MEHR: die Status-Filterliste (Paket „Rechnungsstatus",
   *    Entscheid Dirk 06.09.2026). Der gewählte Filter ist ein Bildschirm und steht
   *    deshalb in der Adresse — genau wie der gewählte Bereich der Verwaltung. `null`
   *    heisst „Alle Rechnungen"; die Hauptnavigation führt auf `/rechnungen` und damit
   *    dorthin (siehe `zielRoute`).
   */
  const rechnungsFilter = route.name === "rechnungen"
    ? (route.filter ?? ALLE_RECHNUNGSFILTER) : ALLE_RECHNUNGSFILTER;
  const rechnungenBereich = useBereichRechnungen({
    aktiv: route.name === "rechnungen",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    arbeitsunternehmenId,
    /* Die eingeschalteten Status-Gruppen aus der Adresse. Anders als bei Angebot und
       Auftrag ist das eine LISTE: Die Chip-Leiste der Rechnungen ist mehrfach wählbar,
       weil ihr Server-Weg den Status wiederholbar nimmt. Kein Segment heisst „alle vier"
       (die Vorgabe), das Segment `keine` heisst „keine". */
    filter: rechnungsFilter,
    rechnungId: route.name === "rechnungen" ? (route.id ?? null) : null,
    markiertId: markiert("rechnungen"),
    // Das Ende der bereichsinternen Auftrag→Rechnung-Naht (siehe oben).
    ausAuftrag: rechnungAusAuftrag,
    onAusAuftragAufgenommen: rechnungAusAuftragVerbraucht,
    // Einen Beleg aufschlagen — die Chip-Auswahl BLEIBT (siehe die Angebote oben).
    onOeffnen: (rechnungId) => navigiere({
      name: "rechnungen",
      ...(rechnungsFilter.length >= ALLE_RECHNUNGSFILTER.length
        ? {} : { filter: rechnungsFilter }),
      ...(rechnungId === null ? {} : { id: rechnungId }),
    }),
    // Ein Filterwechsel klappt den Beleg zu, weil er in der neuen Liste nicht stehen muss.
    // „Alle vier" ergibt bewusst kein Segment: `/rechnungen` IST alle Rechnungen.
    onFilter: (filter) => navigiere({
      name: "rechnungen",
      ...(filter.length >= ALLE_RECHNUNGSFILTER.length ? {} : { filter }),
    }),
    onOrdnerOeffnen: toFolder("rechnungen"),
    registriereVerlassenWaechter,
  });

  /*
   * Der Dienste-Status (Etappe 3) — dieselbe Bauart wie die Bereiche darüber, nur ohne
   * Filter, ohne Blätterung und ohne jeden Schreibweg: Er misst beim Öffnen einmal und
   * danach auf Knopfdruck. Ein Hook ist er aus demselben Grund wie sie: Spalte und Detail
   * teilen sich EINEN Stand — die eine Messung trägt beide Seiten.
   */
  const schnittstellenBereich = useSchnittstellen({
    aktiv: spalte === "schnittstellen",
    client,
    // Nur für die Kürzel-Chips der Kundensuche im Aircall-Panel — dieselbe Liste, die die
    // Bereiche bekommen (und dieselbe stabile Referenz, siehe LEERE_UNTERNEHMEN).
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    dienstSchluessel: route.name === "schnittstellen" ? (route.dienst ?? null) : null,
    // Label-Filter im Instanz-Detail nur für ADMIN (Nachtrag 1g, F156).
    istAdmin,
    arbeitsunternehmenId,
    aircallCompanyId: sitzung?.aircallCompanyId ?? null,
    onOeffnen: (dienst) => navigiere(
      { name: "schnittstellen", ...(dienst === null ? {} : { dienst }) }),
  });

  /** Die offene Einstellungsgruppe (Etappe 3) — `null` heisst „noch keine gewählt"
   *  (Adresse `/einstellungen`). Kein Hook nötig: Die Einstellungen holen nichts vom
   *  Server, ihre Werte stehen in der Sitzung. */
  const einstellungsGruppe: EinstellungsGruppe | null =
    route.name === "einstellungen" ? (route.gruppe ?? null) : null;

  /*
   * Die OBJEKT-Bereiche der Verwaltung (Etappe 2, Aufgabe 2).
   *
   * Sie sind nach derselben Bauart gebaut wie die Bereiche der Etappe 1 darüber und stehen
   * aus demselben Grund hier: Ihr Zustand muss den BEREICHSWECHSEL überleben (Filter,
   * Suchtext und Blätterung sind die Sicht auf die Liste, keine angefangene Eingabe), und
   * ihre Verdrahtung — Adresse, Navigation, Verlassen-Wächter — gehört ohnehin hierher.
   * Die ausführliche Begründung steht an `VerwaltungBereichTeile` (gemeinsam.tsx).
   *
   * Seit dem Entscheid vom 06.09.2026 füllen sie nur noch EINE Stelle des Bildschirms: den
   * Inhalt der Verwaltung, links die Liste und rechts das gewählte Objekt. Die Spalte
   * trägt wieder allein das Bereichs-Untermenü.
   *
   * `aktiv` ist hier doppelt verriegelt: der offene Bereich UND die ADMIN-Rolle. Für alle
   * anderen ist die Verwaltung mit 403 verschlossen (der Server prüft das) — eine Abfrage
   * loszuschicken, die nur eine 403 zurückbringen kann, wäre in der kurzen Spanne bis zur
   * Umleitung (siehe der Wächter-Effekt oben) sinnlos.
   */
  const verwaltungsBereich: VerwaltungsBereich | null =
    route.name === "verwaltung" ? (route.bereich ?? null) : null;
  const verwaltungsObjektId = route.name === "verwaltung" ? (route.id ?? null) : null;
  /** Der Bereichswechsel bleibt INNERHALB der Verwaltung — die Herkunft („aus dem
   *  Artikelkatalog hierher") überlebt ihn, wie der Reiterwechsel in der Akte. */
  const verwaltungHerkunft = route.name === "verwaltung" && route.herkunft !== undefined
    ? { herkunft: route.herkunft } : {};
  /** Dieselbe Herkunft als blosser Wert — für den Rückweg am Kopf der Verwaltungs-Spalte
   *  (Aufgabe 3, siehe dort). `null` heisst „ohne fremde Herkunft hergekommen". */
  const verwaltungFremdeHerkunft: Herkunft | null =
    route.name === "verwaltung" ? (route.herkunft ?? null) : null;

  const verwaltungBenutzer = useVerwaltungBenutzer({
    aktiv: istAdmin && verwaltungsBereich === "benutzer",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    benutzerId: verwaltungsBereich === "benutzer" ? verwaltungsObjektId : null,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "benutzer", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /*
   * Review H4 (Entscheid 679): Die Sitzung lädt einmal; nach dem Speichern der Stammdaten eines Unternehmens holt sie ihre
   * Unternehmen neu, damit Umschalter, Wortmarke und Aktionsfarbe den neuen Stand ohne Neuladen zeigen. Nur die Unternehmen
   * — Benutzer und Fensterwahl bleiben, wie sie sind. Scheitert die Abfrage, bleibt der alte Stand bis zum nächsten Laden:
   * Gespeichert ist ja, und die Verwaltung hat das bereits quittiert.
   */
  function unternehmenDerSitzungAuffrischen(): void {
    client.getSession()
      .then((antwort) => {
        const companies = antwort.companies;
        if (companies === undefined) return;
        setSitzung((s) => (s === null ? s : { ...s, companies }));
      })
      .catch(() => {});
  }

  const verwaltungUnternehmen = useVerwaltungUnternehmen({
    aktiv: istAdmin && verwaltungsBereich === "unternehmen",
    client,
    unternehmenId: verwaltungsBereich === "unternehmen" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "unternehmen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
    istAdmin,
    onStammdatenGespeichert: unternehmenDerSitzungAuffrischen,
  });

  /*
   * Die vier Objekt-Bereiche der Aufgabe 3 — gleiche Bauart, gleiche Verriegelung: der
   * offene Bereich UND die ADMIN-Rolle. Welche Bereiche eine Objektebene bekommen und
   * warum, steht in VERWALTUNGS_BEREICHE (views/Verwaltung.tsx).
   */
  const verwaltungKonditionen = useVerwaltungKonditionen({
    aktiv: istAdmin && verwaltungsBereich === "zahlungskonditionen",
    client,
    konditionId:
      verwaltungsBereich === "zahlungskonditionen" ? verwaltungsObjektId : null,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "zahlungskonditionen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /* Der Artikelstamm bekommt KEINEN Verlassen-Wächter: Sein einziger Bearbeitungszustand
     ist der Formularzustand des geteilten ArtikelDialogs, und der wohnt dort — dieselbe
     Lage und dieselbe Entscheidung wie im Artikel-Bereich der Etappe 1. */
  const verwaltungArtikel = useVerwaltungArtikel({
    aktiv: istAdmin && verwaltungsBereich === "artikel",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    artikelId: verwaltungsBereich === "artikel" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "artikel", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
  });

  const verwaltungTextbausteine = useVerwaltungTextbausteine({
    aktiv: istAdmin && verwaltungsBereich === "textbausteine",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    textbausteinId: verwaltungsBereich === "textbausteine" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "textbausteine", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  const verwaltungAngebotsvorlagen = useVerwaltungAngebotsvorlagen({
    aktiv: istAdmin && verwaltungsBereich === "angebotsvorlagen",
    client,
    companies: sitzung?.companies ?? LEERE_UNTERNEHMEN,
    vorlageId: verwaltungsBereich === "angebotsvorlagen" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "angebotsvorlagen", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /* Der Layout-Editor (Etappe 1) — dieselbe Bauart. Der Sprung „Unternehmen öffnen"
     führt in die Unternehmensverwaltung: Akzentfarbe und Logo werden dort gepflegt (R2). */
  const verwaltungLayout = useVerwaltungLayout({
    aktiv: istAdmin && verwaltungsBereich === "layout",
    client,
    layoutId: verwaltungsBereich === "layout" ? verwaltungsObjektId : null,
    arbeitsunternehmenId,
    onOeffnen: (id) => navigiere({
      name: "verwaltung", bereich: "layout", ...(id === null ? {} : { id }),
      ...verwaltungHerkunft }),
    onUnternehmen: (companyId) => navigiere({
      name: "verwaltung", bereich: "unternehmen", id: companyId, ...verwaltungHerkunft }),
    registriereVerlassenWaechter,
  });

  /**
   * Welcher Verwaltungsbereich seine eigene Objektliste mitbringt — als TABELLE über
   * `VerwaltungsBereich` und nicht als Kette von Vergleichen, aus demselben Grund wie bei
   * `spaltenListen` weiter unten: Kommt ein Bereich hinzu, ist dieses Objekt unvollständig
   * und der Bau bricht ab. `null` heisst „steht rechts als Ganzes" — dieselbe Aussage, die
   * `objektliste` in VERWALTUNGS_BEREICHE trifft; dass beide übereinstimmen, hält
   * packages/web/test/verwaltungsObjektliste.test.tsx fest.
   */
  const verwaltungsObjektBereiche: Record<VerwaltungsBereich, VerwaltungBereichTeile | null>
    = {
      benutzer: verwaltungBenutzer,
      unternehmen: verwaltungUnternehmen,
      zahlungskonditionen: verwaltungKonditionen,
      teams: null,
      faelligkeitstickets: null,
      "ansprechpartner-rollen": null,
      "ansprechpartner-kategorien": null,
      dateitypen: null,
      steuersaetze: null,
      // Lieferanten Strang B (Aufgabe 9): Kontierungsstammdaten ohne Objektebene.
      konten: null,
      kostenstellen: null,
      vertragsarten: null,
      artikel: verwaltungArtikel,
      textbausteine: verwaltungTextbausteine,
      angebotsvorlagen: verwaltungAngebotsvorlagen,
      layout: verwaltungLayout,
      nummerierung: null,
      nummernkreise: null,
    };
  const offenerObjektBereich = verwaltungsBereich === null
    ? null : verwaltungsObjektBereiche[verwaltungsBereich];

  /**
   * ABMELDEN LÖST DAS GERÄT (F105, Schlussreview): Hat dieser Browser ein Push-Abo, wird es VOR
   * dem Abmelden beim Dienst entfernt — sonst bekäme der Abgemeldete auf einem geteilten Gerät
   * weiter Mitteilungen. Das Abo im Browser bleibt bestehen: Wer sich hier als Nächstes anmeldet,
   * übernimmt es über das Abstimmen nach der Anmeldung. Bestmöglich und befristet (je 3 s) —
   * ein Fehler hält das Abmelden nie auf.
   * Die Fensterwahl (R3, Dirk 391) gehört dem Abgemeldeten: Wer sich im selben Tab anmeldet, beginnt mit seiner
   * eigenen Vorgabe statt mit dem Unternehmen des Vorgängers.
   */
  function abmelden(): void {
    vergissFensterUnternehmen();
    const geraetLoesen = aktuellesAbo(browserUmgebung())
      .then((abo) => (abo === null ? undefined : mitFrist(client.unregisterPush(abo.endpoint))))
      .catch(() => undefined);
    void geraetLoesen
      .then(() => fetch("/auth/logout", { method: "POST", credentials: "include" }))
      .finally(() => window.location.assign("/"));
  }

  /**
   * Einstellungen.tsx hat applyTheme bereits angewendet, bevor onChange hierher kommt —
   * die Auswahl wirkt also schon. Der Server-Aufruf läuft nebenläufig; scheitert er,
   * bleibt die Darstellung wie gewählt und es erscheint nur ein Hinweis (keine
   * Datenänderung, die zurückgerollt werden müsste).
   */
  function aendereEinstellungen(p: Preferences): void {
    setSitzung((s) => (s === null ? s : { ...s, user: { ...s.user, ...p } }));
    setEinstellungenFehler(null);
    client.updatePreferences(p).catch(() => {
      setEinstellungenFehler(
        "Die Darstellung konnte nicht gespeichert werden. Sie bleibt wie gewählt, bis " +
        "die Verbindung wieder klappt.");
    });
  }

  /**
   * Der WECHSEL des aktiven Unternehmens (Spec Mandantentrennung, R4, Entscheid 366): Verlassen-
   * Wächter fragen, die Wahl DIESES Fensters merken, sie als Vorgabe für neue Fenster speichern
   * (E369) und das Fenster neu laden — so beginnt jede Liste, jedes Panel und jeder Vorfilter
   * im neuen Zusammenhang. Eine offene Akte ohne Beziehung zum neuen Unternehmen schließt zur
   * Kundenliste; `ziel` führt woandershin (Dublettenhinweis: Akte des Treffers, Reiter Stammdaten).
   * `suche` nimmt die Kundensuche über das Neuladen mit (weitere Treffer, R22) — erst nach der
   * Freigabe abgelegt, ein abgebrochener Wechsel legt nichts ab.
   * `ungefragt`: Der Aufrufer hat selbst festgestellt, dass nichts verloren geht (Dublettentreffer
   * mit nur dem Suchnamen, R20) — dann fragt kein Wächter, und der Browser-Schutz fällt wie nach
   * einer Frage erst unmittelbar vor dem Neuladen (Review Aufgabe 16 M1).
   * Scheitert das Speichern, gilt die Wahl trotzdem für dieses Fenster.
   */
  async function wechsleUnternehmen(
    id: string, o: { ziel?: Route; suche?: string; ungefragt?: boolean } = {},
  ): Promise<void> {
    const { ziel, suche } = o;
    if (sitzung === null || (arbeit?.id === id && ziel === undefined)) return;
    const antwort = o.ungefragt === true ? "weiter" : await fragVerlassen();
    if (antwort === "bleiben") return;
    if (suche !== undefined) merkeKundensuche(suche);
    merkeFensterUnternehmen(id);
    const { colorScheme, density, rememberPanels } = sitzung.user;
    await client.updatePreferences({
      colorScheme: colorScheme ?? "SYSTEM", density: density ?? "COMPACT",
      rememberPanels: rememberPanels ?? true, workCompanyId: id,
    }).catch(() => undefined);
    // Dieselbe Regel wie beim Zeichnen: Ein Kunde ohne sichtbare Beziehung bleibt stehen (E357).
    const fremdeAkte = route.name === "kunde" && akte !== null
      && istFremdeAkte(akte, sitzung.companies, id);
    // Nur nach einer Frage des Wächters (oder der Feststellung des Aufrufers, `ungefragt`) widerspricht der Browser
    // dem Neuladen nicht ein zweites Mal; ohne Wächter ist er die einzige Absicherung einer offenen Maske.
    if (antwort === "weiter") verlassenFreigeben();
    fenster.neuLaden(ziel !== undefined ? routeToPath(ziel)
      : fremdeAkte ? routeToPath({ name: "kundenliste" }) : undefined);
  }

  if (sitzungFehler !== null) {
    return (
      <div className="app__ladeseite" role="alert">
        {sitzungFehler.message}
      </div>
    );
  }
  if (ladeSitzung || sitzung === null) {
    return <div className="app__ladeseite">Lädt …</div>;
  }

  /** Die Wortmarke zeigt das ARBEITSunternehmen — das, unter dem gerade gearbeitet wird. */
  const firma = arbeit;
  /** Der Hinweis auf die Aircall-Anbindung (E365) — dieselbe Quelle wie unter Schnittstellen. */
  const aircallAnbindung = aircallHinweis(sitzung.companies, sitzung.aircallCompanyId ?? null);

  /** Wer in den Tickets „Ich" ist (Zuständig-Vorgabe, Avatar, eigene Kommentare). Die Sitzung
   *  führt die Kennung als wahlfrei — ohne sie zeichnen Bereich und Akte-Reiter nicht (statt
   *  mit einer leeren Kennung als Zuständigem anzulegen). */
  const ichTickets = sitzung.user.id === undefined ? null
    : { id: sitzung.user.id, name: sitzung.user.name ?? sitzung.user.email ?? "" };

  /** Die Adresse sagt, welcher Navigationspunkt hervorgehoben ist — nicht ein zweiter
   *  Zustand daneben, der mit ihr auseinanderlaufen könnte. Alles, was kein eigenes Ziel
   *  hat (Akte, Anlegen, unbekannt), zählt zu „Kunden". Beide Lieferantenbereiche zählen zum
   *  „Einkauf“ (Entscheid 516b) — auch beim Direktaufruf aus Kachel, Handlungsbedarf oder Rückweg. */
  const navZiel: NavigationsZiel =
    // Im Zuschnitt steht der Belegdesigner für den Layout-Bereich der Verwaltung.
    zuschnitt === "belegdesign" && route.name === "verwaltung" && route.bereich === "layout"
      ? "belegdesign"
      : isSupplierRoute(route) ? "einkauf"
      : route.name === "cockpit" ? "cockpit"
      : route.name === "liquidity" ? "finanzen"
      : route.name === "verwaltung" ? "verwaltung"
      : route.name === "artikel" ? "artikel"
        : route.name === "einstellungen" ? "einstellungen"
          : route.name === "schnittstellen" ? "schnittstellen"
            : route.name === "angebote" ? "angebote"
              : route.name === "auftraege" ? "auftraege"
                : route.name === "rechnungen" ? "rechnungen"
                  : route.name === "tickets" ? "tickets"
                  : route.name === "kalender" ? "kalender"
                  : "kunden";

  const rueckweg = zurueckWeg(route);
  /** Sieht dieser Benutzer Lieferantendaten (LEAD/ADMIN, Entscheid 496)? Navigation, Bereiche, Akte-Reiter. */
  const supplierView = seesSupplierData(sitzung.user.role);
  /** Der Ticketweg der Lieferantenobjekte („Ticket anlegen“ im „⋯“, PF19) — nur mit eigener Kennung. */
  const lieferantenTicket = ichTickets === null ? {}
    : { ticket: { client, ich: ichTickets, arbeitsunternehmenId: firma?.id ?? null } };

  /**
   * Die Herkunft der GEÖFFNETEN AKTE, weitergereicht an jede Navigation, die INNERHALB
   * dieser Akte bleibt (der Reiterwechsel).
   *
   * Anders als `vorgangId`, die beim Reiterwechsel bewusst verfällt: Ein anderer Reiter ist
   * derselbe Kunde, man hat die Akte nicht verlassen — und wer aus der Angebotssuche kam,
   * will nach einem Blick auf die Stammdaten immer noch dorthin zurück. Verfallen soll sie
   * erst, wenn die Adresse selbst wechselt (Neuladen, Zurück-Taste, ein anderer Kunde) —
   * und das tut sie von selbst, weil `parseRoute` das Feld nicht kennt.
   */
  const akteHerkunft = route.name === "kunde" && route.herkunft !== undefined
    ? { herkunft: route.herkunft, ...(route.originFilter === undefined ? {} : { originFilter: route.originFilter }) } : {};

  /** Wohin „Einkauf“ führt (Entscheid 516b, Fixrunde 1 W1): `purchasingTabFor`. */
  const purchasingTab: SupplierArea = purchasingTabFor(route, sitzung.user.id);

  const navProps = {
    istAdmin: sitzung.user.role === "ADMIN",
    aktiv: navZiel,
    purchasingTab,
    onNavigate: (ziel: NavigationsZiel): void => {
      setVerwaltungMeldung(null);
      navigiere(zielRoute(ziel, purchasingTab));
    },
  };

  /**
   * Die Listen hinter den Spalteninhalten — als TABELLE über `SpaltenInhalt`, nicht als
   * Kette von Vergleichen (`spalte === "kunden" ? … : null`).
   *
   * Der Unterschied ist der Typprüfer: Kommt ein Spalteninhalt hinzu, ist dieses Objekt
   * unvollständig und der Bau bricht ab. Eine Vergleichskette liesse den neuen Inhalt
   * stumm auf `null` fallen — die Spalte bliebe leer, und niemand sähe je einen Fehler
   * (Review-Prüfpunkt aus Aufgabe 1; dieselbe Überlegung wie bei `spalteFuer` selbst).
   *
   * Die Einträge sind ELEMENTE, keine gezeichneten Ansichten: Was nicht in die Spalte
   * kommt, wird auch nicht gebaut.
   */
  const spaltenListen: Record<SpaltenInhalt, ReactNode> = {
    /*
     * Preis dieser Lösung, bewusst so gewählt: Die Liste wird beim Verlassen des
     * Kunden-Ziels AUSGEHÄNGT und beim Rückweg neu gebaut. Der Suchtext überlebt das von
     * selbst, weil er hier oben in App.tsx liegt (`search`); die Bildlaufposition der
     * Trefferliste geht verloren. Sie zu erhalten hiesse, die Liste dauerhaft im Baum zu
     * lassen und nur zu verstecken — ein zweiter Zustandskanal für einen Komfort, den
     * niemand verlangt hat. Festgehalten in packages/web/test/listenspalte.test.tsx.
     */
    kunden: (
      <KundenListe
        items={items}
        loading={listeLaedt}
        error={listeFehler}
        search={search}
        onSearch={setSearch}
        selectedId={selectedId ?? markiert("kunde")}
        // Der WECHSEL von einem Kunden zum nächsten behält den offenen Reiter
        // (Befund Dirk, 05.09.2026: wer Angebote durchsieht, will beim nächsten
        // Kunden wieder Angebote sehen, nicht die Übersicht). Genau dafür steht der
        // Reiter in der Adresse — der Wechsel ist dieselbe Adresse mit anderer
        // Kennung, und weil ALLE Reiter universell sind (AKTEN_TABS ist eine feste
        // Liste), gibt es dabei keinen Kunden, der den Reiter nicht hätte.
        // `activeTab` ist ausserhalb einer Akte "uebersicht" — die ERSTE Öffnung aus
        // der Liste heraus schlägt also weiterhin die Übersicht auf.
        onSelect={(id) => navigiere({ name: "kunde", id, tab: activeTab })}
        onNeu={() => setPartnerNeuAn(routeToPath(route))}
        companies={sitzung.companies}
        // Die Rollen-Chips (C9, Aufgabe 6/22): Wahl gemerkt, Rollen der Zeile zum aktiven Unternehmen.
        rollenWahl={rollenWahl}
        onRollenWahl={chooseRoles}
        aktivId={arbeitsunternehmenId}
        // Weitere Treffer in anderen Unternehmen (E370, R22) — nur hier, nicht in den Kundenwahlen
        // beim Anlegen eines Belegs. Der Wechsel landet auf der Kundenliste mit derselben Suche.
        weitereTreffer={weitereTreffer}
        onWechseln={(id) => { void wechsleUnternehmen(id, { ziel: { name: "kundenliste" }, suche: search }); }}
      />
    ),
    /*
     * Der ARTIKEL-Bereich (Entscheid Dirk 23.09.2026): seine Spaltenliste — Filterkopf,
     * Artikelzeilen, Blätterung. Der Hook liefert Spalte und Detail als zwei Stücke, weil
     * beide sich EINEN Zustand teilen (siehe views/BereichArtikel.tsx).
     */
    artikel: artikelBereich.spalte,
    // Die Positionsspalte der Liquidität; ohne Lieferantensicht keine Spalte, nur der Sperrhinweis im Inhalt.
    liquidity: areaLocked("liquidity", sitzung.user.role) ? null : liquidityArea.column,
    /*
     * Der ANGEBOTS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 5): seine Spaltenliste —
     * Filterkopf, Angebotszeilen, Blätterung. Derselbe Grund wie beim Artikel: Der Hook
     * liefert Spalte und Detail als zwei Stücke, weil beide sich EINEN Zustand teilen
     * (siehe views/BereichAngebote.tsx).
     */
    angebote: angeboteBereich.spalte,
    /*
     * Der AUFTRAGS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 6): seine Spaltenliste —
     * Filterkopf, Auftragszeilen, Blätterung. Derselbe Grund wie beim Angebot: Der Hook
     * liefert Spalte und Detail als zwei Stücke, weil beide sich EINEN Zustand teilen
     * (siehe views/BereichAuftraege.tsx).
     */
    auftraege: auftraegeBereich.spalte,
    /*
     * Der RECHNUNGS-Bereich (Entscheid Dirk 23.09.2026, Aufgabe 7): seine Spaltenliste —
     * Filterkopf mit der mehrfach wählbaren Chip-Leiste, Belegzeilen, Blätterung. Derselbe
     * Grund wie bei den drei Schwestern: Der Hook liefert Spalte und Detail als zwei
     * Stücke, weil beide sich EINEN Zustand teilen (siehe views/BereichRechnungen.tsx).
     */
    rechnungen: rechnungenBereich.spalte,
    /*
     * Die Schnittstellen (Etappe 3): die gemessene Dienstliste, samt dem
     * Aktualisieren-Knopf und dem Stand der Messung in ihrem Kopf — dort, wo in den
     * Bereichen der Etappe 1 das Suchfeld steht (was die Liste bestimmt, gehört zu ihr).
     */
    schnittstellen: schnittstellenBereich.spalte,
    /*
     * Die Einstellungen (Etappe 3): das Untermenü ihrer Gruppen. Dieselbe Gestalt wie das
     * Bereichs-Untermenü der Verwaltung, nur ohne zweite Ebene — eine Einstellungsgruppe
     * hat keine Objekte.
     */
    einstellungen: (
      <EinstellungenSpalte
        gruppe={einstellungsGruppe}
        onGruppe={(gruppe) => navigiere({ name: "einstellungen", gruppe })}
      />
    ),
    /*
     * Die Verwaltung (Etappe 2, Aufgabe 1): das Untermenü ihrer Bereiche.
     *
     * Nur für ADMIN — aus demselben Grund, aus dem der Navigationseintrag nur ADMIN
     * bekommt: Der Riegel liegt beim Server (403 an jeder Route), die Oberfläche soll
     * niemanden erst auf einen Bildschirm führen, der ihm ausschliesslich Fehler zeigt.
     * `null` heisst „keine Spalte" — der Rahmen zeichnet sie dann gar nicht erst (der
     * Umleitungs-Effekt oben schickt Nicht-Administratoren im selben Takt zur
     * Kundenliste).
     */
    verwaltung: sitzung.user.role === "ADMIN"
      ? (
        <VerwaltungsSpalte
          bereich={verwaltungsBereich}
          // Der Bereichswechsel bleibt INNERHALB der Verwaltung — die Herkunft
          // („aus dem Artikelkatalog hierher") überlebt ihn, wie beim Reiterwechsel
          // in der Akte. Ein WECHSEL lässt das gewählte Objekt fallen: Die Kennung
          // gehört dem Bereich, aus dem sie stammt.
          {...(zuschnitt === "belegdesign" ? { nurBereiche: BELEGDESIGN_VERWALTUNG } : {})}
          onBereich={(bereich) => navigiere({
            name: "verwaltung", bereich, ...verwaltungHerkunft })}
          /*
           * Der Weg zurück zur fremden HERKUNFT (Aufgabe 3) — heute genau einer: „Pflege in
           * der Verwaltung" aus dem Artikelkatalog. Er steht am Kopf der Spalte, weil der
           * Rahmen über der Spalte keinen Rückweg zeichnet (siehe MeSchale: dort ist man
           * normalerweise am Ursprung) — und BREIT gibt es den Kopf-Knopf des Rahmens
           * überhaupt nicht. Ohne diesen Knopf wäre der Weg aus der Artikelpflege in den
           * Katalog auf dem Schreibtisch also gar nicht gehbar.
           *
           * Auf dem iPhone trägt ihn seit dem 06.09.2026 der Rahmen: Dort zeigt
           * `/verwaltung/artikel` den INHALT (die Liste steht seither rechts, nicht mehr in
           * der Spalte), und über dem Inhalt zeichnet der Rahmen seinen Rückweg — mit
           * derselben Beschriftung, aus derselben Herkunft. Doppelt steht er nie: Der
           * Einspalter zeigt entweder die Spalte ODER den Inhalt.
           */
          {...(verwaltungFremdeHerkunft === null ? {} : { rueckweg: {
            beschriftung: ZURUECK_BESCHRIFTUNG[verwaltungFremdeHerkunft],
            aktion: () => navigiere({ name: verwaltungFremdeHerkunft }),
          } })}
        />
      )
      : null,
  };

  return (
    /*
     * Der Gastgeber der Objektpanels („Öffnen statt Springen“, Spec F4): um den Rahmen und die Panels darüber, damit jede
     * Stelle — bis tief in Akte, Reiter und Dialoge — ein Objekt über `useObjektPanel` öffnet oder anspringt. Die Panels
     * liegen über der Ansicht, die Adresse bleibt; jede Ebene ist ein Verlaufseintrag (696).
     */
    <ObjektPanelGastgeber route={route} navigiere={navigiere} client={client} companies={sitzung.companies}
      sitzungsUnternehmen={arbeitsunternehmenId}
      rechte={{ supplierView, ich: ichTickets === null ? null : { ...ichTickets, istAdmin }, zuschnittVoll: zuschnitt === "voll" }}>
      <MeSchale
        /*
         * Die Wortmarke geht in den eigenen Steckplatz des Rahmens und nicht in `header`:
         * Sie muss das ERSTE Kind der Kopfleiste sein — vor allem, was die Anwendung
         * beisteuert, und auf dem Telefon auch vor dem Rückweg-Knopf des Rahmens selbst
         * (Fixrunde 1, Entscheid des Controllers 08.09.2026).
         */
        wortmarke={
          <Wortmarke firma={firma} umgebung={umgebung} zuschnitt={zuschnitt}
            onHome={() => navigiere(zielRoute(zuschnitt === "belegdesign" ? "belegdesign" : "cockpit", PURCHASING_TAB_DEFAULT))} />
        }
        header={
          <Kopfzeile user={sitzung.user} firma={firma} onLogout={abmelden}
            onOpenSettings={() => setEinstellungenOffen(true)}
            umgebung={umgebung} routenschluessel={routeToPath(route)} {...navProps}
            /* Die Akte eines Partners hat ihr eigenes Handbuchkapitel (Entscheid 677); die unbekannte Seite hat keinen
               Bereich und führt auf die Startseite des Handbuchs (Zuordnung „ohne Zuordnung“) — `navZiel` zählt sie
               sonst zu „Kunden“. */
            manualTarget={route.name === "kunde" ? "akte" : route.name === "unbekannt" ? "start" : navZiel}
            unternehmen={sitzung.companies} zuschnitt={zuschnitt}
            onArbeitsunternehmen={(id) => { void wechsleUnternehmen(id); }}
            /* Die Glocke (F106) — nur im vollen Zuschnitt, denn nur dort gibt es Tickets. Ein Eintrag öffnet das Ticket als
               Objektpanel über der Ansicht (sie liest den Gastgeber selbst, PF12); ihr Hinweis führt in die Gruppe
               „Benachrichtigungen" der Einstellungen (F109). */
            glocke={zuschnitt === "voll"
              ? <Glocke client={client}
                  onEinstellungen={() => navigiere({ name: "einstellungen", gruppe: "benachrichtigungen" })} />
              : undefined} />
        }
        /*
         * Die untere Leiste des Telefons — seit dem 10.09.2026 der EINZIGE Ort, an dem die
         * Bereiche ausserhalb der Kopfleiste stehen. Der Steckplatz `nav` (die linke
         * Randspalte) ist mit demselben Entscheid entfallen; die Bereiche stehen auf iPad
         * und Desktop als Reihe IN der Kopfleiste, und der Inhalt bekommt ihre Breite.
         */
        tabBar={<Tableiste aktiv={navZiel} onNavigate={navProps.onNavigate}
          eintraege={bereicheFuer(zuschnitt, supplierView)} />}
        list={
          /*
           * Ohne Liste zeichnet der Rahmen die Spalte gar nicht erst (siehe MeSchale.tsx) —
           * die Ansicht steht dann in voller Breite da, statt neben einem leeren Streifen.
           * WELCHE Liste es ist, entscheidet `spalteFuer` (oben), und die Tabelle
           * `spaltenListen` bildet den Inhalt typgeprüft auf sie ab.
           */
          spalte === null ? null : spaltenListen[spalte]
        }
        detail={
          route.name === "verwaltung" && sitzung.user.role === "ADMIN"
              // Die Verwaltung zeichnet — wie die Bereiche der Etappe 1 — BEIDE Bereiche
              // des Rahmens: ihren Inhalt hier, ihr Bereichs-Untermenü oben in
              // `spaltenListen`.
              ? <Verwaltung
                  client={client}
                  bereich={route.bereich ?? null}
                  companies={sitzung.companies}
                  arbeitsunternehmenId={firma?.id ?? null}
                  registriereVerlassenWaechter={registriereVerlassenWaechter}
                  /*
                   * Ein Objekt-Bereich (Aufgabe 2) — seit dem Entscheid vom 06.09.2026
                   * mit BEIDEN Stücken hier: links seine Objektliste, rechts das gewählte
                   * Objekt. Bis dahin stand die Liste als zweite Ebene in der Spalte; sie
                   * war dort neben dem Bereichs-Untermenü redundant und zu schmal.
                   *
                   * `objektOffen` ist die Faltung des Einspalters: Mit gewähltem Objekt
                   * steht auf schmalem Gerät nur es da (siehe ObjektBereich).
                   */
                  {...(offenerObjektBereich === null ? {} : { objektBereich: (
                    <ObjektBereich teile={offenerObjektBereich}
                      objektOffen={verwaltungsObjektId !== null} />
                  ) })}
                />
            /*
             * Die OBJEKTBEREICHE füllen wieder ZWEI Bereiche des Rahmens — Spaltenliste oben
             * in `spaltenListen`, Detail hier (Entscheid Dirk 23.09.2026). Zwischen dem 07.
             * und dem 23.09.2026 stand jeder von ihnen GANZ hier, mit Filterkopf, Liste und
             * Detail untereinander in voller Breite. Mit Aufgabe 7 (Rechnungen) gilt das
             * wieder für alle vier.
             */
            : route.name === "artikel"
              ? artikelBereich.detail
            : route.name === "angebote"
              ? angeboteBereich.detail
            : route.name === "auftraege"
              ? auftraegeBereich.detail
            : route.name === "rechnungen"
              ? rechnungenBereich.detail
            : route.name === "cockpit"
              // Das COCKPIT (601): ohne Hülle direkt in der Spalte, ein Polster gäbe eine Lücke über seiner klebenden Leiste. Im
              // Zuschnitt „belegdesign“ leitet der Einstieg um; der Durchlauf davor fragt die Wand nicht erst ab.
              ? entersDesigner ? null
                : <Cockpit client={client} userId={sitzung.user.id} companies={sitzung.companies} phone={phone} onNavigate={navigiere}
                  registriereVerlassenWaechter={registriereVerlassenWaechter} />
            : route.name === "tickets"
              // Die TICKETS (Aufgabe 17): Board | Liste | Zeitachse in voller Breite, der Arbeitsdialog
              // über `/tickets/<id>` bzw. `/tickets/<ansicht>/<id>`; „Zur Akte" springt in den Tickets-Reiter des Kunden.
              ? ichTickets === null
                ? <p className="app__hinweis">Lädt …</p>
                : <BereichTickets client={client} companies={sitzung.companies}
                  registriereVerlassenWaechter={registriereVerlassenWaechter}
                  ich={{ ...ichTickets, istAdmin, supplierView }}
                  arbeitsunternehmenId={firma?.id ?? null}
                  route={{ ansicht: route.ansicht ?? "board", id: route.id ?? null }}
                  onNavigiere={(r) => navigiere({ name: "tickets",
                    ...(r.ansicht === "board" ? {} : { ansicht: r.ansicht }),
                    ...(r.id === null ? {} : { id: r.id }) })} />
            : route.name === "kalender"
              // Der KALENDER (Terminebene 10, T22): Sicht und Datum stehen in der Adresse; Blättern ersetzt den Eintrag, ein
              // Sichtwechsel legt einen an. Ein Termin springt wie in den Tickets in seinen Reiter (Quelle „Termine“).
              ? ichTickets === null
                ? <p className="app__hinweis">Lädt …</p>
                : <BereichKalender client={client} companies={sitzung.companies}
                  registriereVerlassenWaechter={registriereVerlassenWaechter}
                  ich={{ ...ichTickets, istAdmin, supplierView }}
                  arbeitsunternehmenId={firma?.id ?? null}
                  route={{ sicht: route.sicht ?? null, datum: route.datum ?? null, ticketId: route.ticketId ?? null }}
                  onNavigiere={(r, ersetzen) => navigiere({ name: "kalender",
                    ...(r.sicht === null ? {} : { sicht: r.sicht }),
                    ...(r.datum === null ? {} : { datum: r.datum }),
                    ...(r.ticketId === null ? {} : { ticketId: r.ticketId }) }, ersetzen)} />
            : isSupplierRoute(route)
              // Die LIEFERANTENBEREICHE (Aufgabe 22): ohne Lieferantensicht der Sperrhinweis statt der Liste, und es
              // wird nichts geladen (Router-Wächter `areaLocked`).
              ? areaLocked(route.name, sitzung.user.role)
                ? <Hinweis art="info" className="app__meldung">{SUPPLIER_TEXT_WEB.locked}</Hinweis>
                // Der EINKAUF (Entscheid 516b): die Reiter „Lieferverträge | Eingangsrechnungen“ über dem Bereich der
                // Adresse; gemerkt wird der angezeigte Reiter (W1), ein Reiterwechsel öffnet den Bereich ohne Chips. Die
                // Status-Chips stehen in der Adresse (A-W3): Ein Chip navigiert, der Rückweg findet sie wieder.
                : <PurchasingArea active={route.name} userId={sitzung.user.id} onSelect={(tab) => navigiere({ name: tab })}>
                  {route.name === "liefervertraege"
                    ? <BereichLiefervertraege client={client} unternehmen={sitzung.companies}
                        aktivesUnternehmen={firma?.id ?? null} {...lieferantenTicket}
                        states={statusseZuFilter(route.filter ?? [], SUPPLIER_CONTRACT_FILTER)}
                        onStates={(states) => navigiere(supplierAreaRoute("liefervertraege",
                          filterForStates(states, SUPPLIER_CONTRACT_FILTER)))} />
                    : <BereichEingangsrechnungen client={client} unternehmen={sitzung.companies}
                        aktivesUnternehmen={firma?.id ?? null} {...lieferantenTicket}
                        states={statusseZuFilter(route.filter ?? [], INCOMING_FILTER)}
                        onStates={(states) => navigiere(supplierAreaRoute("eingangsrechnungen",
                          filterForStates(states, INCOMING_FILTER)))} />}
                </PurchasingArea>
            : route.name === "liquidity"
              // Finanzen › Liquidität: ohne Lieferantensicht der Sperrhinweis statt der Ansicht (`areaLocked`), es lädt nichts.
              ? areaLocked(route.name, sitzung.user.role)
                ? <Hinweis art="info" className="app__meldung">{LIQUIDITY_TEXT_WEB.locked}</Hinweis>
                : liquidityArea.detail
            : route.name === "schnittstellen"
              // Der Dienste-Status (Etappe 3) zeichnet — wie die Bereiche darüber — BEIDE
              // Bereiche des Rahmens: sein Detail hier, seine Dienstliste oben in
              // `spaltenListen`.
              ? schnittstellenBereich.detail
            : route.name === "einstellungen"
              // Der Menüpunkt „Einstellungen" zeigt DIESELBE Komponente wie der Dialog aus
              // dem Benutzermenü (unten) — mit denselben Werten aus der Sitzung und
              // demselben Speicherweg. Kein zweiter Stand, nur ein zweiter Zugang: der
              // eine für „ich gehe in meine Einstellungen", der andere für „schnell die
              // Ansicht umstellen, ohne den Bildschirm zu verlassen".
              //
              // Seit Etappe 3 zeigt er GENAU EINE Gruppe — die aus der Adresse, gewählt im
              // Untermenü der Spalte. Ohne Gruppe steht hier der Hinweis, wie in der
              // Verwaltung ohne Bereich; der Dialog bleibt davon unberührt und zeigt
              // weiterhin alle drei (er bekommt kein `gruppe`).
              ? <section className="app__einstellungen" aria-label="Einstellungen">
                  <h2 className="app__einstellungen-titel">Einstellungen</h2>
                  {einstellungenFehler !== null && (
                    <Hinweis art="fehler">{einstellungenFehler}</Hinweis>
                  )}
                  {einstellungsGruppe === null
                    ? <p className="app__hinweis">Bitte links eine Gruppe wählen.</p>
                    : <Einstellungen
                        colorScheme={sitzung.user.colorScheme ?? "SYSTEM"}
                        density={sitzung.user.density ?? "COMPACT"}
                        rememberPanels={sitzung.user.rememberPanels ?? true}
                        fontScale={sitzung.user.fontScale ?? "AUTO"}
                        onChange={aendereEinstellungen}
                        gruppe={einstellungsGruppe}
                        benachrichtigungen={<EinstellungenBenachrichtigungen client={client} />}
                        fremdsysteme={<FremdkontenBlock client={client} admin={sitzung.user.role === "ADMIN"} />}
                        kalender={<EinstellungenKalender client={client} supplierView={supplierView} />}
                      />}
                </section>
              : route.name === "unbekannt"
                ? (
                  <Hinweis art="fehler" className="app__meldung" aktionen={
                    <KiKnopf onClick={() => navigiere({ name: "kundenliste" })}>
                      {PARTNER_TEXT.toList}
                    </KiKnopf>}>
                    Diese Seite gibt es nicht.
                  </Hinweis>
                )
                : selectedId === null
                  ? verwaltungMeldung !== null
                    ? <Hinweis art="fehler" className="app__meldung">{verwaltungMeldung}</Hinweis>
                    : <p className="app__hinweis">{PARTNER_TEXT.noneSelected}</p>
                  : akteFehler !== null
                    ? <Hinweis art="fehler" className="app__meldung">{akteFehler.message}</Hinweis>
                    : akte !== null && firma !== null
                      && istFremdeAkte(akte, sitzung.companies, firma.id)
                      // E360/R18: Ein Kunde ohne Beziehung zum aktiven Unternehmen — keine Akte,
                      // nur der Hinweis, wohin er gehört, mit dem Weg dorthin.
                      ? <FremdeAkte kunde={akte} companies={sitzung.companies}
                          onWechseln={(id) => { void wechsleUnternehmen(id); }} />
                    : akte !== null
                      ? <KundenAkte kunde={akte} activeTab={activeTab}
                          arbeitsunternehmenId={firma?.id ?? null}
                          onTab={(tab) => navigiere({
                            name: "kunde", id: selectedId, tab, ...akteHerkunft })}
                          companies={sitzung.companies}
                          // Der in der Kundenübersicht angeklickte Vorgang. Er steht in
                          // der ROUTE und nirgends sonst: Ein Tabwechsel oder die
                          // Zurück-Taste erzeugen ein Ziel ohne ihn, und damit verfällt er
                          // von selbst — ohne einen zweiten Zustand, der aufgeräumt werden
                          // müsste (vgl. die Verfallsregeln in KundenAkte.tsx).
                          {...(route.name === "kunde" && route.vorgangId !== undefined
                            ? { oeffneVorgangId: route.vorgangId } : {})}
                          // Ordner (`null` = Wurzel) und Quelle eines Sprungs aus der Übersicht reisen wie
                          // `vorgangId` und verfallen mit ihm.
                          {...(route.name === "kunde" && route.folderId !== undefined
                            ? { oeffneOrdnerId: route.folderId } : {})}
                          {...(route.name === "kunde" && route.source !== undefined
                            ? { jumpSource: route.source } : {})}
                          // Das Unternehmen eines Vorgangs von außerhalb der Akte (Ticketbezug, Bereich): zuschalten.
                          {...(route.name === "kunde" && route.companyId !== undefined
                            ? { openCompanyId: route.companyId } : {})}
                          // Die Lieferantenreiter (Aufgabe 22): nur mit Lieferantensicht und nach den Rollen der Firma.
                          supplierView={supplierView}
                          liefervertraege={{ client }}
                          eingangsrechnungen={{ client }}
                          // Die Monitor-Kachel der Übersicht (Spec vom 08.09.2026, Entscheid
                          // 422): derselbe Klient, nur die zwei Wege, die sie braucht.
                          monitor={{ client }}
                          // Die Übersicht (Spec „Kundenübersicht in Kacheln“): Ein Sprung in
                          // einen Vorgang läuft über die Route (Reiter, Vorgang, Ordner, Quelle).
                          uebersicht={{ client, onSprung: (s) => navigiere({ name: "kunde", id: selectedId,
                            tab: s.tab, ...(s.vorgangId === undefined ? {} : { vorgangId: s.vorgangId }),
                            ...(s.folderId === undefined ? {} : { folderId: s.folderId }), source: s.source,
                            ...akteHerkunft }) }}
                          // Der Tickets-Reiter (F33): Geöffnet wird als Objektpanel über der Akte (Öffnen statt Springen A9).
                          {...(ichTickets === null ? {} : { tickets: { client, ich: ichTickets } })}
                          // Der Adressen-Reiter (Adressen Etappe 1): derselbe Klient, nur
                          // die fünf Wege, die der Baustein braucht — dazu der Ruf, der
                          // die Akte neu holt, weil die Hauptanschrift im Kopf aus den
                          // Adressen ABGELEITET ist.
                          adressen={{ client, onNeuLaden: akteNeuLaden }}
                          // Der Konzern-Reiter (Adressen Etappe 2): derselbe Klient, nur
                          // die drei Wege, die er braucht. Der frische Kundensatz aus dem
                          // Schreibweg geht an die Akte UND wirft die Kundenliste neu an:
                          // Dort steht künftig „Tochter von <Eltern>", und die Liste
                          // erführe von der Änderung sonst erst bei der nächsten Suche.
                          konzern={{
                            client,
                            onAktualisiert: (k) => {
                              setAkte((alt) => withRoleFacts(k, alt));
                              setListeVersion((v) => v + 1);
                            },
                          }}
                          // Der Sprung in eine andere Akte — wie früher „Zum Kunden" (heute `ObjektVerweis` zur Akte)
                          // aus den Bereichslisten: über den Router, mit „uebersicht" als
                          // Landeplatz. Der Reiter, aus dem gesprungen wurde, reist NICHT
                          // mit: Der neue Kunde hat seinen eigenen Konzern, und dort stünde
                          // womöglich eine leere Seite.
                          onKunde={(id) => navigiere(
                            { name: "kunde", id, tab: "uebersicht" })}
                          stammdaten={{
                            client,
                            darfUebernehmen: darfSperreUebernehmen(sitzung.user),
                            // Der Aircall-Account am Kunden: nur ADMIN pflegt (R7).
                            istAdmin: sitzung.user.role === "ADMIN",
                            aircall: {
                              sicht: aircallSicht(sitzung.user.role === "ADMIN", firma?.id ?? null,
                                sitzung.aircallCompanyId ?? null),
                              hinweis: aircallAnbindung,
                            },
                            // Rollen und Nummern stehen auch in der Geschäftspartnerliste (Kennzeichen, Zeilenname): neu laden (A-K1).
                            onAktualisiert: (k) => { setAkte((alt) => withRoleFacts(k, alt)); setListeVersion((v) => v + 1); },
                            registriereVerlassenWaechter,
                          }}
                          ansprechpartner={{
                            client,
                            darfUebernehmen: darfSperreUebernehmen(sitzung.user),
                            registriereVerlassenWaechter,
                          }}
                          notizen={{
                            client,
                            // Eigentümer-Regel des Notiz-Reiters: bearbeiten/löschen darf
                            // der Verfasser — oder jede/r ADMIN. Der Server prüft dasselbe
                            // noch einmal in der Route (der Wächter hier blendet nur Knöpfe
                            // aus, die ohnehin mit 403 abgewiesen würden). Die Kennung ist im
                            // Sitzungstyp optional; fehlt sie (dürfte nie), blendet der ""-
                            // Rückfall die Knöpfe aus statt sie fälschlich zu zeigen — die
                            // ausfallsichere Richtung, den echten Riegel trägt ohnehin die Route.
                            angemeldeterBenutzerId: sitzung.user.id ?? "",
                            istAdmin: sitzung.user.role === "ADMIN",
                          }}
                          dateien={{ client }}
                          angebote={{ client }}
                          auftraege={{
                            client,
                            // Lösen von Abrechnungsquellen an nicht aktiven Aufträgen: nur
                            // ADMIN (Entscheid Dirk 29.09.2026, Punkt 7).
                            istAdmin: sitzung.user.role === "ADMIN",
                          }}
                          rechnungen={{ client }}
                          wichtiges={{ client }} />
                      : <p className="app__hinweis">Lädt …</p>
        }
        context={
          /*
           * Ohne Kontext zeichnet der Rahmen die Spalte gar nicht erst (siehe MeSchale.tsx)
           * — die Ansicht bekommt dann ihre Breite dazu, statt neben einem Streifen zu
           * stehen, der überall dasselbe „Kein Kunde ausgewählt." sagt. WO sie steht,
           * entscheidet `zeigtKundenKontext` (oben).
           */
          zeigtKundenKontext(route)
            ? <Kontext kunde={selectedId === null ? null : akte} />
            : null
        }
        panels={panels}
        onPanelsChange={setPanels}
        /*
         * Was der iPhone-Einspalter zeigt: die Spalte oder das Detail (siehe
         * `spalteZuerst`).
         *
         * Seit dem 23.09.2026 haben die Objektbereiche ihre Spalte zurück, also gibt es dort
         * wieder etwas zu falten. Die Ausnahmen, die das mit sich bringt, sind die
         * Anlege-Masken von Angebot und Auftrag: Sie stehen im DETAIL, haben aber keine
         * eigene Adresse (ein Lesezeichen auf ein offenes Formular wäre sinnlos, siehe
         * router.ts) — ohne einen Zusatz blieben sie auf dem iPhone unsichtbar, und die
         * Knöpfe „Neues Angebot" bzw. „Neuer Auftrag" täten scheinbar nichts. Die
         * Adressprüfung davor ist dabei nicht Beiwerk (Fixrunde 1 zu Aufgabe 3 vom
         * 05.09.2026): Ohne sie entschiede ein Zustand EINES BEREICHS über den ganzen Rahmen
         * — wer die Maske öffnete und dann auf „Kunden" wechselte, sähe dort auf dem iPhone
         * gar keine Kundenliste mehr.
         *
         * Die ANGEBOTS-Klausel kam mit Aufgabe 5 zurück, die der Aufträge folgt hier mit
         * Aufgabe 6. Der ARTIKEL-Bereich braucht keine: Er kennt keine Anlege-Maske —
         * angelegt wird allein in der Verwaltung —, und sein einziger adressloser Zustand
         * (der Änderungsdialog des Bausteins) steht ohnehin nur über einem GEÖFFNETEN
         * Artikel, wo der Einspalter schon das Detail zeigt. Der RECHNUNGS-Bereich (Aufgabe
         * 7) braucht ebenfalls keine: Eine Rechnung entsteht am Auftrag bzw. als Korrektur,
         * nicht auf Zuruf — es gibt dort keine Anlege-Maske. Damit sind alle vier
         * Objektbereiche hier bedacht.
         */
        showList={(route.name === "liquidity" && liquidityArea.showColumn) || (spalteZuerst(route)
          && !(route.name === "angebote" && angeboteBereich.anlageOffen)
          && !(route.name === "auftraege" && auftraegeBereich.anlageOffen))}
        // Der Rückweg des iPhone-Einspalters — WOHIN er führt, weiss nur die Adresse samt
        // ihrer Herkunft (siehe zurueckWeg oben). Ohne Rückweg bekommt der Rahmen nichts
        // und zeichnet keinen Knopf.
        {...(rueckweg === null ? {} : {
          zurueck: {
            beschriftung: zurueckBeschriftung(rueckweg),
            aktion: () => navigiere(zurueckRoute(rueckweg)),
          },
        })}
        // Die Spalte der Liquidität ist am Telefon ein Zwischenschritt: Der Rückweg steht über ihr und führt zum Inhalt.
        {...(route.name === "liquidity" && liquidityArea.back !== undefined
          ? { zurueck: liquidityArea.back, zurueckBeiListe: true } : {})}
      />
      {partnerNeuOffen && (
        // „Neuer Geschäftspartner“ im Modalpanel (Entscheid 524), über der Liste und der Ansicht darunter.
        <KundeAnlegen
          client={client}
          companies={sitzung.companies}
          arbeitsunternehmenId={firma?.id ?? null}
          // Die Rollenwahl kommt aus den aktiven Chips der Liste (C9).
          rollenVorgabe={rollenWahl.roles}
          onWechseln={(companyId, kundeId, { ungefragt }) => {
            void wechsleUnternehmen(companyId,
              { ziel: { name: "kunde", id: kundeId, tab: "stammdaten" }, ungefragt });
          }}
          onFertig={(id) => {
            // Die Liste kennt den neuen Kunden noch nicht — neu laden, dann in die Akte springen. Das Panel fällt mit
            // der Adresse; bleibt sie gleich (der Treffer ist die offene Akte), schließt es nach dem Wächter selbst.
            setListeVersion((v) => v + 1);
            // Ist der Treffer die offene Akte (etwa nach „Rolle Kunde hinzufügen“), bleibt ihre Kennung gleich: Sie lädt von
            // selbst nicht neu, und Kopf, Rollenkennzeichen und Reiter blieben auf altem Stand.
            if (id === selectedId) akteNeuLaden();
            const ziel: Route = { name: "kunde", id, tab: "uebersicht" };
            if (routeToPath(ziel) !== routeToPath(route)) { navigiere(ziel); return; }
            void fragVerlassen().then((antwort) => { if (antwort !== "bleiben") setPartnerNeuAn(null); });
          }}
          onAbbrechen={() => {
            setPartnerNeuAn(null);
            // Der Direktaufruf hat keine Ansicht darunter: zurück auf die Liste (ersetzt den Eintrag /kunden/neu, den
            // die Zurück-Taste sonst wieder aufschlüge).
            if (route.name === "kundeNeu") navigiere({ name: "kundenliste" }, true);
          }}
          registriereVerlassenWaechter={registriereVerlassenWaechter}
        />
      )}
      {/*
        * Der schwebende Hinweis (Entscheid Dirk 27, me-design 3311f91): `Hinweis
        * schwebend` steht unten mittig über dem Inhalt, ohne ihn zu verschieben, und mehrere
        * stapeln sich von selbst nach oben (früher der Nachbau `.app__schwebend`).
        */}
      {neueVersionBereit && (
        // Kein Modal (Hausregel „Modal-Bearbeitung" gilt für Bearbeiten/Anlegen, nicht für
        // diesen Hinweis): Er schwebt über dem Inhalt, verdeckt keine
        // Eingabe und blockiert nichts — wer weiterarbeiten will, kann es einfach tun.
        // „Später" ist sein Schliessen; ein Kreuz daneben wäre ein zweiter gleicher Ausgang.
        <Hinweis art="warnung" schwebend aktionen={<>
          <KiKnopf art="primaer" onClick={() => window.location.reload()}>Neu laden</KiKnopf>
          <KiKnopf onClick={() => setNeueVersionBereit(false)}>Später</KiKnopf>
        </>}>
          Eine neue Version des Kundeninfo ist bereit. Bitte lade die Seite neu, sonst können
          Teile der Anwendung nicht mehr geladen werden.
        </Hinweis>
      )}
      {einstellungenOffen && (
        /* Die EINE benannte Ausnahme von der Hausregel „striktes Modalpanel" (Prüfliste in
         * striktesModal.test.tsx): Darstellung ist keine Bearbeitung — jede Wahl wirkt
         * sofort, es gibt keinen Entwurf und kein Speichern; deshalb Schliessen-Kreuz.
         * Übergang P12-5 (docs/ME-DESIGN.md): Rahmen, Schleier und Kreuz sind hier noch
         * selbst gebaut (role="dialog", eigener Fokusfang). Die Hülle `Modal` ist strikt
         * (Klick daneben schliesst nicht); die Ausnahme braucht `Dialog` ohne `strikt` —
         * eigenes Paket, weil die Prüfliste striktesModal.test.tsx daran hängt. */
        <div className="app__modal-hintergrund" role="presentation"
          onClick={() => setEinstellungenOffen(false)}>
          <div ref={einstellungenRef} tabIndex={-1} className="app__modal" role="dialog"
            aria-modal="true" aria-label="Darstellung"
            onClick={(e) => e.stopPropagation()}>
            <div className="app__modal-kopf">
              <h2>Darstellung</h2>
              <button type="button" className="app__modal-schliessen" aria-label="Schließen"
                onClick={() => setEinstellungenOffen(false)}>
                <Icon name="xmark" size={16} />
              </button>
            </div>
            {einstellungenFehler !== null && (
              <Hinweis art="fehler">{einstellungenFehler}</Hinweis>
            )}
            <Einstellungen
              colorScheme={sitzung.user.colorScheme ?? "SYSTEM"}
              density={sitzung.user.density ?? "COMPACT"}
              rememberPanels={sitzung.user.rememberPanels ?? true}
              fontScale={sitzung.user.fontScale ?? "AUTO"}
              onChange={aendereEinstellungen}
            />
          </div>
        </div>
      )}
    </ObjektPanelGastgeber>
  );
}
